<script setup lang="ts">
import * as XLSX from 'xlsx'
import type { ECOption } from '@/plugins/echarts'
import { notifyError, to_wd_dd_MMMM_hh_mm, to_wd_dd_MMMM_yyyy } from '@/utils/helpers'
import { VDateInput } from 'vuetify/labs/VDateInput'

definePage({
    meta: {
        id: 'vgco.palettes',
        title: 'Suivi des palettes',
        icon: 'mdi-forklift',
        drawerIndex: 7,
        requiresAuth: true,
        requiresSiteAccess: 'vegco'
    },
})

/* ── Types ──────────────────────────────────────────────────────────── */

declare interface CTPalette {
    codeProduit: string
    noLot: string
    noItem: string
    statut: number
    noClient: string
    nomClient: string
    noFournisseur: string
    nomFournisseur: string
    quantite: number
    quantiteInventaire: number
    paletteId: string
    noCommandeAchat: string
    noCommandeVente: string
    poClient: string
    idEtat: string
    dateReception: string
    dateRecolte: string
    dateExpedition: string
    poidsBrut: number
    poidsContenant: number
    poidsPalette: number
}

declare interface PaletteRow extends CTPalette {
    uid: string
    produit: string
    fournisseur: string
    client: string
    statutLibellé: string
    étatLibellé: string
    dateReceptionF: string
    dateRecolteF: string
    dateExpeditionF: string
    jourReception: string
    semaineReception: string
    jourRecolte: string
    poidsNet: number
    recherche: string
}

declare interface ColPalette {
    title: string
    key: string
    /** Propriété de la ligne à utiliser comme valeur de filtre (défaut: key). */
    cléFiltre?: string
    /** Nom du filtre API correspondant, si supporté par l'endpoint. */
    apiFiltre?: 'paletteIds' | 'noLots' | 'noFournisseur' | 'codesProduits' | 'noCommandesVente' | 'noCommandesAchat' | 'nomClient' | 'poClient' | 'statut' | 'etat'
    type?: 'texte' | 'nombre' | 'poids' | 'date' | 'statut' | 'etat'
    align?: 'start' | 'end'
    width?: string
}

declare interface FiltreLocal {
    clé: string
    libellé: string
    valeur: string
    exclure: boolean
}

declare interface Agrégat {
    clé: string
    valeurs: string[]
    palettes: number
    quantite: number
    quantiteInventaire: number
    poidsNet: number
    poidsBrut: number
    lots: number
    produits: number
    clients: number
    fournisseurs: number
}

/* ── Constantes ─────────────────────────────────────────────────────── */

const LIMITE_API = 1000

const STATUTS = [
    { title: 'Tous les statuts', value: null },
    { title: 'En inventaire', value: 1 },
    { title: 'Sortie / expédiée', value: 0 },
]

const ETATS = [
    { title: 'Tous les états', value: null },
    { title: 'Valide', value: 'V' },
    { title: 'Quarantaine', value: 'Q' },
]

const COLONNES: ColPalette[] = [
    { title: 'ID palette', key: 'paletteId', apiFiltre: 'paletteIds', width: '130px' },
    { title: 'Réception', key: 'dateReception', type: 'date', width: '190px' },
    { title: 'Récolte', key: 'dateRecolte', type: 'date', width: '170px' },
    { title: 'Date d\'expédition', key: 'dateExpedition', type: 'date', width: '190px' },
    { title: 'Code produit', key: 'codeProduit', apiFiltre: 'codesProduits', width: '120px' },
    { title: 'Produit', key: 'produit', width: '200px' },
    { title: 'No lot', key: 'noLot', apiFiltre: 'noLots', width: '150px' },
    { title: 'No item', key: 'noItem', width: '110px' },
    { title: 'Fournisseur', key: 'fournisseur', cléFiltre: 'noFournisseur', apiFiltre: 'noFournisseur', width: '190px' },
    { title: 'Client', key: 'client', cléFiltre: 'nomClient', apiFiltre: 'nomClient', width: '170px' },
    { title: 'Cmd achat', key: 'noCommandeAchat', apiFiltre: 'noCommandesAchat', width: '110px' },
    { title: 'Cmd vente', key: 'noCommandeVente', apiFiltre: 'noCommandesVente', width: '110px' },
    { title: 'PO client', key: 'poClient', apiFiltre: 'poClient', width: '120px' },
    { title: 'Statut', key: 'statutLibellé', cléFiltre: 'statut', apiFiltre: 'statut', type: 'statut', width: '130px' },
    { title: 'État', key: 'étatLibellé', cléFiltre: 'idEtat', apiFiltre: 'etat', type: 'etat', width: '110px' },
    { title: 'Unités', key: 'quantite', type: 'nombre', align: 'end', width: '90px' },
    { title: 'Unités inv.', key: 'quantiteInventaire', type: 'nombre', align: 'end', width: '100px' },
    { title: 'Poids net (lb)', key: 'poidsNet', type: 'poids', align: 'end', width: '120px' },
    { title: 'Poids brut (lb)', key: 'poidsBrut', type: 'poids', align: 'end', width: '120px' },
]

const COLONNES_DÉFAUT = [
    'paletteId', 'dateReception', 'dateRecolte', 'dateExpedition', 'codeProduit', 'produit', 'noLot',
    'fournisseur', 'client', 'noCommandeAchat', 'noCommandeVente', 'statutLibellé', 'étatLibellé',
    'quantite', 'poidsNet',
]

const OPTIONS_GROUPEMENT = [
    { title: 'Jour de réception', value: 'jourReception' },
    { title: 'Semaine de réception', value: 'semaineReception' },
    { title: 'Jour de récolte', value: 'jourRecolte' },
    { title: 'Fournisseur', value: 'fournisseur' },
    { title: 'Client', value: 'client' },
    { title: 'Code produit', value: 'codeProduit' },
    { title: 'Produit', value: 'produit' },
    { title: 'No lot', value: 'noLot' },
    { title: 'Commande d\'achat', value: 'noCommandeAchat' },
    { title: 'Commande de vente', value: 'noCommandeVente' },
    { title: 'Statut', value: 'statutLibellé' },
    { title: 'État', value: 'étatLibellé' },
]

const LIBELLÉS_GROUPES: Record<string, string> = Object.fromEntries(
    OPTIONS_GROUPEMENT.map(o => [o.value, o.title]))

/* ── État ───────────────────────────────────────────────────────────── */

const theme = useTheme()
const { smAndDown } = useDisplay()
const storeU = useUtilisateurStore()

const menuPropsFiltresMobile = {
    location: 'bottom' as const,
    maxHeight: 320,
    closeOnContentClick: false,
}

const isLoading = ref(false)
const palettes = ref<PaletteRow[]>([])

/** Replié d'emblée sur mobile : le tableau doit rester visible à l'ouverture de la page. */
const afficherFiltres = ref(!smAndDown.value)
/** Le bandeau de totaux est facultatif sur mobile : il prend trop de place pour rester ouvert par défaut. */
const afficherStatsMobile = ref(false)
const recherche = ref('')
const vue = ref<'detail' | 'sommaire' | 'flux'>('detail')
const groupBy = ref<{ key: string, order: 'asc' | 'desc' }[]>([])
const groupementChoisi = ref<string[]>([])
const colonnesActives = ref<string[]>([...COLONNES_DÉFAUT])
const itemsParPage = ref(50)
const filtresLocaux = ref<FiltreLocal[]>([])

const filtres = reactive({
    dateMin: '',
    dateMax: '',
    dateRecolteMin: '',
    dateRecolteMax: '',
    dateExpeditionMin: '',
    dateExpeditionMax: '',
    paletteIds: [] as string[],
    noLots: [] as string[],
    noFournisseur: [] as string[],
    codesProduits: [] as string[],
    noCommandesVente: [] as string[],
    noCommandesAchat: [] as string[],
    nomClient: [] as string[],
    poClient: [] as string[],
    statut: null as number | null,
    etat: null as string | null,
})

/** Valeurs rencontrées, accumulées entre les requêtes pour alimenter les listes déroulantes. */
const catalogue = reactive({
    fournisseurs: new Map<string, string>(),
    produits: new Map<string, string>(),
    lots: new Set<string>(),
    clients: new Map<string, string>(),
    nomsClients: new Set<string>(),
    commandesAchat: new Set<string>(),
    commandesVente: new Set<string>(),
    poClients: new Set<string>(),
})

/* ── Formatage ──────────────────────────────────────────────────────── */

const nfEntier = new Intl.NumberFormat('fr-CA', { maximumFractionDigits: 0 })
const nfPoids = new Intl.NumberFormat('fr-CA', { minimumFractionDigits: 1, maximumFractionDigits: 1 })

function fmtNombre(n: number): string {
    return nfEntier.format(n ?? 0)
}

function fmtPoids(n: number): string {
    return nfPoids.format(n ?? 0)
}

function isoJour(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function numéroSemaine(d: Date): number {
    const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
    const jour = date.getUTCDay() || 7
    date.setUTCDate(date.getUTCDate() + 4 - jour)
    const début = new Date(Date.UTC(date.getUTCFullYear(), 0, 1))
    return Math.ceil(((date.getTime() - début.getTime()) / 86400000 + 1) / 7)
}

/* ── Chargement ─────────────────────────────────────────────────────── */

function normaliser(valeurs: string[]): string[] {
    const sortie: string[] = []
    for (const v of valeurs) {
        for (const morceau of String(v ?? '').split(/[\s,;]+/)) {
            const t = morceau.trim()
            if (t && !sortie.includes(t))
                sortie.push(t)
        }
    }
    return sortie
}

/**
 * Les noms de clients et les PO contiennent des espaces : les découper mot à mot ferait
 * chercher chaque mot séparément côté API (LIKE ... OR ...) et ramènerait d'autres clients.
 * Seuls le point-virgule et le retour de ligne séparent donc les valeurs.
 */
function normaliserTexte(valeurs: string[]): string[] {
    const sortie: string[] = []
    for (const v of valeurs) {
        for (const morceau of String(v ?? '').split(/[;\n]+/)) {
            const t = morceau.trim()
            if (t && !sortie.includes(t))
                sortie.push(t)
        }
    }
    return sortie
}

/**
 * Un ID de palette complet ressemble à `000495605112` (12 chiffres) ou `V000001435`
 * (préfixe alphabétique + chiffres). Tout le reste est traité comme une recherche partielle.
 */
function estIdPaletteComplet(valeur: string): boolean {
    return /^[A-Za-z]?\d{9,}$/.test(valeur)
}

/**
 * L'API filtre les ID de palettes en égalité stricte. Dès qu'une valeur saisie est partielle,
 * on n'envoie plus rien au serveur et la liste complète est filtrée localement en « contient ».
 */
const paletteIdsPartiels = computed(() => normaliser(filtres.paletteIds).filter(v => !estIdPaletteComplet(v)))
const rechercheIdLocale = computed(() => paletteIdsPartiels.value.length > 0)

function construireParams(): URLSearchParams {
    const params = new URLSearchParams()

    const ajouter = (nom: string, valeurs: string[]) => {
        for (const v of normaliser(valeurs))
            params.append(nom, v)
    }
    const ajouterNombres = (nom: string, valeurs: string[]) => {
        for (const v of normaliser(valeurs))
            if (!Number.isNaN(Number(v)))
                params.append(nom, v)
    }
    const ajouterTextes = (nom: string, valeurs: string[]) => {
        for (const v of normaliserTexte(valeurs))
            params.append(nom, v)
    }

    if (!rechercheIdLocale.value)
        ajouter('paletteIds', filtres.paletteIds)
    ajouter('noLots', filtres.noLots)
    ajouter('noFournisseur', filtres.noFournisseur)
    ajouter('codesProduits', filtres.codesProduits)
    ajouterTextes('nomClient', filtres.nomClient)
    ajouterTextes('poClient', filtres.poClient)
    ajouterNombres('noCommandesVente', filtres.noCommandesVente)
    ajouterNombres('noCommandesAchat', filtres.noCommandesAchat)

    if (filtres.statut !== null)
        params.append('statut', String(filtres.statut))
    if (filtres.etat)
        params.append('etat', filtres.etat)

    // L'API tronque les bornes .Max à minuit (SqlQueryHelpers.DateMax) : on ajoute une journée
    // pour inclure la date choisie en entier.
    const ajouterDateMax = (nom: string, valeur: string) => {
        if (!valeur) return
        const d = new Date(`${valeur}T00:00:00`)
        d.setDate(d.getDate() + 1)
        params.append(nom, isoJour(d))
    }

    if (filtres.dateMin)
        params.append('dateReceptionMin', filtres.dateMin)
    ajouterDateMax('dateReceptionMax', filtres.dateMax)

    if (filtres.dateRecolteMin)
        params.append('dateRecolteMin', filtres.dateRecolteMin)
    ajouterDateMax('dateRecolteMax', filtres.dateRecolteMax)

    if (filtres.dateExpeditionMin)
        params.append('dateExpiditionMin', filtres.dateExpeditionMin)
    ajouterDateMax('dateExpiditionMax', filtres.dateExpeditionMax)

    return params
}

function préparerLigne(p: CTPalette, index: number): PaletteRow {
    const réception = new Date(p.dateReception)
    const récolte = new Date(p.dateRecolte)
    const expédition = new Date(p.dateExpedition)
    const récolteValide = récolte.getFullYear() > 2000
    const réceptionValide = réception.getFullYear() > 2000
    const expéditionValide = expédition.getFullYear() > 2000

    const produitStore = storeU.getVegcoProduit(p.codeProduit)
    const produit = produitStore?.description || p.codeProduit || '—'
    const fournisseur = p.nomFournisseur?.trim() || p.noFournisseur || '—'
    const client = p.nomClient?.trim() || p.noClient || '—'

    const poidsNet = Math.max(0, (p.poidsBrut || 0) - (p.poidsPalette || 0) - (p.quantite || 0) * (p.poidsContenant || 0))
    const statutLibellé = p.statut === 1 ? 'En inventaire' : p.statut === 0 ? 'Sortie / expédiée' : `Statut ${p.statut}`
    const étatLibellé = p.idEtat === 'Q' ? 'Quarantaine' : p.idEtat === 'V' ? 'Valide' : (p.idEtat || '—')

    const ligne: PaletteRow = {
        ...p,
        uid: `${p.paletteId}-${p.noLot}-${index}`,
        produit,
        fournisseur,
        client,
        statutLibellé,
        étatLibellé,
        dateReceptionF: réceptionValide ? to_wd_dd_MMMM_hh_mm(réception) : '—',
        dateRecolteF: récolteValide ? to_wd_dd_MMMM_yyyy(récolte) : '—',
        dateExpeditionF: expéditionValide ? to_wd_dd_MMMM_hh_mm(expédition) : '—',
        jourReception: réceptionValide ? isoJour(réception) : '—',
        semaineReception: réceptionValide ? `${réception.getFullYear()}-S${String(numéroSemaine(réception)).padStart(2, '0')}` : '—',
        jourRecolte: récolteValide ? isoJour(récolte) : '—',
        poidsNet,
        recherche: '',
    }

    ligne.recherche = [
        p.paletteId, p.noLot, p.noItem, p.codeProduit, produit, fournisseur, p.noFournisseur,
        client, p.noClient, p.noCommandeAchat, p.noCommandeVente, p.poClient, statutLibellé, étatLibellé,
        ligne.jourReception, ligne.jourRecolte, ligne.dateExpeditionF,
    ].join(' ').toLowerCase()

    return ligne
}

function alimenterCatalogue(lignes: PaletteRow[]) {
    for (const l of lignes) {
        if (l.noFournisseur)
            catalogue.fournisseurs.set(l.noFournisseur, `${l.noFournisseur} — ${l.nomFournisseur || '?'}`)
        if (l.codeProduit)
            catalogue.produits.set(l.codeProduit, `${l.codeProduit} — ${l.produit}`)
        if (l.noLot)
            catalogue.lots.add(l.noLot)
        if (l.noClient)
            catalogue.clients.set(l.noClient, `${l.noClient} — ${l.nomClient || '?'}`)
        if (l.nomClient)
            catalogue.nomsClients.add(l.nomClient)
        if (l.noCommandeAchat && l.noCommandeAchat !== '0')
            catalogue.commandesAchat.add(l.noCommandeAchat)
        if (l.noCommandeVente && l.noCommandeVente !== '0')
            catalogue.commandesVente.add(l.noCommandeVente)
        if (l.poClient)
            catalogue.poClients.add(l.poClient)
    }
}

let génération = 0

async function getData() {
    const gen = ++génération
    try {
        isLoading.value = true
        await storeU.fetchVegcoProduitsIfNeeded()

        const response = await apiProphet.get('vgco/palettes', { params: construireParams() })
        if (gen !== génération)
            return

        const lignes = (response.data as CTPalette[]).map(préparerLigne)
        palettes.value = lignes
        alimenterCatalogue(lignes)
    }
    catch (e) {
        if (gen === génération)
            palettes.value = []
        notifyError(e)
    }
    finally {
        if (gen === génération)
            isLoading.value = false
    }
}

/* ── Filtres ────────────────────────────────────────────────────────── */

const lignesFiltrées = computed((): PaletteRow[] => {
    const texte = recherche.value.trim().toLowerCase()
    const fragmentsId = rechercheIdLocale.value
        ? normaliser(filtres.paletteIds).map(v => v.toLowerCase())
        : []

    return palettes.value.filter(l => {
        if (texte && !l.recherche.includes(texte))
            return false

        if (fragmentsId.length > 0) {
            const id = l.paletteId.toLowerCase()
            if (!fragmentsId.some(f => id.includes(f)))
                return false
        }

        for (const f of filtresLocaux.value) {
            const valeur = String((l as any)[f.clé] ?? '')
            if (f.exclure ? valeur === f.valeur : valeur !== f.valeur)
                return false
        }
        return true
    })
})

/* ── Anomalies de poids ─────────────────────────────────────────────── */

/** Nombre minimal de palettes d'un produit sous lequel la notion d'aberrant n'a pas de sens. */
const MIN_PALETTES_ANALYSE = 5
/** Seuil du score z modifié (Iglewicz & Hoaglin) au-delà duquel la valeur est jugée aberrante. */
const SEUIL_Z = 3.5
/** Repli quand la dispersion est nulle : écart relatif à la médiane toléré. */
const ÉCART_RELATIF_MAX = 0.25

declare interface Anomalie {
    type: 'nul' | 'aberrant'
    titre: string
    /** Phrase autoportante, reprise telle quelle dans l'export Excel. */
    libellé: string
    /** Chiffres à l'appui, détaillés en infobulle. */
    détails: { libellé: string, valeur: string }[]
    icone: string
    couleur: string
}

function médiane(valeurs: number[]): number {
    const triées = [...valeurs].sort((a, b) => a - b)
    const milieu = Math.floor(triées.length / 2)
    return triées.length % 2 === 1 ? triées[milieu]! : (triées[milieu - 1]! + triées[milieu]!) / 2
}

/** Poids net ramené à l'unité : seule mesure comparable entre palettes d'un même produit. */
function poidsUnitaire(l: PaletteRow): number {
    return l.poidsNet / Math.max(1, l.quantite || 1)
}

/**
 * Médiane et écart absolu médian (MAD) du poids unitaire, par code produit.
 * Le regroupement par SKU est fait ici, indépendamment des regroupements et filtres choisis :
 * la référence reste donc valable quelle que soit la vue.
 * La médiane et le MAD sont insensibles aux valeurs extrêmes, contrairement à moyenne
 * et écart-type : quelques palettes fautives ne masquent donc pas les autres.
 */
const statsPoidsParProduit = computed(() => {
    const stats = new Map<string, { médiane: number, mad: number, n: number }>()

    const paquets = new Map<string, number[]>()
    for (const l of lignesFiltrées.value) {
        const p = poidsUnitaire(l)
        // Les poids nuls sont signalés à part : les inclure fausserait la référence.
        if (p <= 0)
            continue
        const liste = paquets.get(l.codeProduit)
        if (liste)
            liste.push(p)
        else
            paquets.set(l.codeProduit, [p])
    }

    for (const [k, valeurs] of paquets) {
        const med = médiane(valeurs)
        stats.set(k, { médiane: med, mad: médiane(valeurs.map(v => Math.abs(v - med))), n: valeurs.length })
    }
    return stats
})

function anomaliePoids(l: PaletteRow): Anomalie | null {
    const p = poidsUnitaire(l)
    if (p <= 0) {
        return {
            type: 'nul',
            titre: 'Poids net nul',
            libellé: 'Poids net nul : pesée manquante ou tares supérieures au poids brut.',
            détails: [
                { libellé: 'Poids brut', valeur: `${fmtPoids(l.poidsBrut)} lb` },
                { libellé: 'Tare des contenants', valeur: `${fmtPoids(l.poidsContenant)} lb` },
                { libellé: 'Tare de la palette', valeur: `${fmtPoids(l.poidsPalette)} lb` },
                { libellé: 'Poids net obtenu', valeur: `${fmtPoids(l.poidsNet)} lb` },
            ],
            icone: 'mdi-alert-octagon',
            couleur: 'error',
        }
    }

    const stat = statsPoidsParProduit.value.get(l.codeProduit)
    if (!stat || stat.n < MIN_PALETTES_ANALYSE)
        return null

    const écart = stat.médiane > 0 ? (p - stat.médiane) / stat.médiane : 0
    const aberrant = stat.mad > 0
        ? Math.abs(0.6745 * (p - stat.médiane) / stat.mad) > SEUIL_Z
        : Math.abs(écart) > ÉCART_RELATIF_MAX

    if (!aberrant)
        return null

    const signe = écart >= 0 ? '+' : '−'
    const pourcentage = `${signe}${Math.abs(écart * 100).toFixed(0)}%`
    return {
        type: 'aberrant',
        titre: écart >= 0 ? 'Poids anormalement élevé' : 'Poids anormalement bas',
        libellé: `${fmtPoids(p)} lb/unité contre ${fmtPoids(stat.médiane)} lb pour ce produit `
            + `(${pourcentage}, sur ${stat.n} palettes).`,
        détails: [
            { libellé: 'Poids net', valeur: `${fmtPoids(l.poidsNet)} lb pour ${fmtNombre(l.quantite)} unités` },
            { libellé: 'Poids par unité', valeur: `${fmtPoids(p)} lb` },
            { libellé: `Médiane du produit ${l.codeProduit}`, valeur: `${fmtPoids(stat.médiane)} lb/unité` },
            { libellé: 'Écart à la médiane', valeur: `${pourcentage} (${signe}${fmtPoids(Math.abs(p - stat.médiane))} lb/unité)` },
            { libellé: 'Poids net attendu', valeur: `≈ ${fmtPoids(stat.médiane * Math.max(1, l.quantite || 1))} lb` },
            { libellé: 'Base de comparaison', valeur: `${stat.n} palettes du même code produit` },
        ],
        icone: 'mdi-alert',
        couleur: 'warning',
    }
}

/** Le repérage est toujours actif : passif, il n'enlève aucune ligne du tableau. */
function anomalieAffichée(l: PaletteRow): Anomalie | null {
    return anomaliePoids(l)
}

function anomalieCellule(l: PaletteRow, col: ColPalette): Anomalie | null {
    return col.key === 'poidsNet' ? anomalieAffichée(l) : null
}

/** Teinte discrète sur toute la ligne, pour repérer les anomalies sans lire chaque poids. */
function classeLigne(l: PaletteRow): string {
    const anomalie = anomalieAffichée(l)
    if (!anomalie)
        return ''
    return anomalie.type === 'nul' ? 'ligne-poids-nul' : 'ligne-poids-aberrant'
}

const totaux = computed(() => {
    const lignes = lignesFiltrées.value
    return {
        palettes: lignes.length,
        quantite: lignes.reduce((s, l) => s + (l.quantite || 0), 0),
        quantiteInventaire: lignes.reduce((s, l) => s + (l.quantiteInventaire || 0), 0),
        poidsNet: lignes.reduce((s, l) => s + l.poidsNet, 0),
        poidsBrut: lignes.reduce((s, l) => s + (l.poidsBrut || 0), 0),
        lots: new Set(lignes.map(l => l.noLot).filter(Boolean)).size,
        produits: new Set(lignes.map(l => l.codeProduit).filter(Boolean)).size,
        fournisseurs: new Set(lignes.map(l => l.fournisseur).filter(Boolean)).size,
        clients: new Set(lignes.map(l => l.client).filter(v => v && v !== '—')).size,
    }
})

const limiteAtteinte = computed(() => palettes.value.length >= LIMITE_API)

const filtresAPIActifs = computed(() => {
    // `clé` identifie chaque puce de façon stable (contenu, pas position) : indispensable pour
    // le `:key` du v-for, sans quoi Vuetify réutilise l'état interne « fermé » d'une puce
    // retirée pour une autre puce qui se décale à sa place (elle disparaît alors à tort,
    // sans que son filtre ne soit réellement retiré).
    const chips: { clé: string, libellé: string, retirer: () => void }[] = []

    const ajouterListe = (libellé: string, clé: 'paletteIds' | 'noLots' | 'noFournisseur' | 'codesProduits' | 'noCommandesVente' | 'noCommandesAchat' | 'nomClient' | 'poClient') => {
        for (const v of filtres[clé]) {
            const partiel = clé === 'paletteIds' && rechercheIdLocale.value
            chips.push({
                clé: `${clé}:${v}`,
                libellé: partiel ? `${libellé} contient: ${v}` : `${libellé}: ${v}`,
                retirer: () => { filtres[clé] = filtres[clé].filter(x => x !== v); getData() },
            })
        }
    }

    ajouterListe('ID palette', 'paletteIds')
    ajouterListe('Lot', 'noLots')
    ajouterListe('Fournisseur', 'noFournisseur')
    ajouterListe('Produit', 'codesProduits')
    ajouterListe('Client', 'nomClient')
    ajouterListe('Cmd vente', 'noCommandesVente')
    ajouterListe('Cmd achat', 'noCommandesAchat')
    ajouterListe('PO client', 'poClient')

    if (filtres.statut !== null) {
        const s = STATUTS.find(x => x.value === filtres.statut)
        chips.push({ clé: 'statut', libellé: `Statut: ${s?.title ?? filtres.statut}`, retirer: () => { filtres.statut = null; getData() } })
    }

    if (filtres.etat !== null) {
        const e = ETATS.find(x => x.value === filtres.etat)
        chips.push({ clé: 'etat', libellé: `État: ${e?.title ?? filtres.etat}`, retirer: () => { filtres.etat = null; getData() } })
    }

    return chips
})

function réinitialiser() {
    filtres.paletteIds = []
    filtres.noLots = []
    filtres.noFournisseur = []
    filtres.codesProduits = []
    filtres.noCommandesVente = []
    filtres.noCommandesAchat = []
    filtres.nomClient = []
    filtres.poClient = []
    filtres.statut = null
    filtres.etat = null
    filtres.dateRecolteMin = ''
    filtres.dateRecolteMax = ''
    filtres.dateExpeditionMin = ''
    filtres.dateExpeditionMax = ''
    filtresLocaux.value = []
    recherche.value = ''
    appliquerPériode(7)
}

/* ── Raccourci producteur ───────────────────────────────────────────── */

/**
 * Correspondance site producteur → fournisseur CyberTrace.
 * Miroir de `CyberTrace.GetSiteFromCyberTraceFournisseurAsync` côté C#.
 */
const FOURNISSEUR_PAR_SITE: Record<string, string> = {
    DLFL: '400002',
    PHVW: '400003',
    JPLG: '400004',
    FHVW: '400005',
}

/** Non nul seulement si le site connecté est une ferme, pas l'usine (VGCO). */
const fournisseurUtilisateur = computed(() =>
    FOURNISSEUR_PAR_SITE[storeU.siteSélectionné?.lettresDappel ?? ''] ?? null)

const mesPalettesActif = computed(() =>
    fournisseurUtilisateur.value !== null
    && filtres.noFournisseur.length === 1
    && filtres.noFournisseur[0] === fournisseurUtilisateur.value)

function basculerMesPalettes() {
    const no = fournisseurUtilisateur.value
    if (!no)
        return
    filtres.noFournisseur = mesPalettesActif.value ? [] : [no]
    getData()
}

function appliquerPériode(jours: number | null) {
    if (jours === null) {
        filtres.dateMin = ''
        filtres.dateMax = ''
        getData()
        return
    }
    const fin = new Date()
    const début = new Date()
    début.setDate(début.getDate() - (jours - 1))
    filtres.dateMin = isoJour(début)
    filtres.dateMax = isoJour(fin)
    getData()
}

/**
 * `v-date-input` en mode `multiple="range"` prend/rend un tableau de dates plutôt que deux bornes.
 * Le picker ne colore que les jours réellement présents dans le tableau : il faut donc lister
 * chaque jour entre les deux bornes (pas seulement les deux extrémités) pour voir la plage remplie.
 */
function plageDepuisChamps(min: string, max: string): Date[] {
    if (!min || !max)
        return []
    const dates: Date[] = []
    const jour = new Date(`${min}T00:00:00`)
    const fin = new Date(`${max}T00:00:00`)
    while (jour <= fin) {
        dates.push(new Date(jour))
        jour.setDate(jour.getDate() + 1)
    }
    return dates
}

const plageRéception = computed<Date[]>({
    get: () => plageDepuisChamps(filtres.dateMin, filtres.dateMax),
    set: (dates) => {
        filtres.dateMin = dates.length > 0 ? isoJour(dates[0]) : ''
        filtres.dateMax = dates.length > 0 ? isoJour(dates[dates.length - 1]) : ''
        getData()
    },
})

const plageRécolte = computed<Date[]>({
    get: () => plageDepuisChamps(filtres.dateRecolteMin, filtres.dateRecolteMax),
    set: (dates) => {
        filtres.dateRecolteMin = dates.length > 0 ? isoJour(dates[0]) : ''
        filtres.dateRecolteMax = dates.length > 0 ? isoJour(dates[dates.length - 1]) : ''
        getData()
    },
})

const plageExpédition = computed<Date[]>({
    get: () => plageDepuisChamps(filtres.dateExpeditionMin, filtres.dateExpeditionMax),
    set: (dates) => {
        filtres.dateExpeditionMin = dates.length > 0 ? isoJour(dates[0]) : ''
        filtres.dateExpeditionMax = dates.length > 0 ? isoJour(dates[dates.length - 1]) : ''
        getData()
    },
})

/* `v-btn-toggle` ne sait pas gérer `null` comme valeur : on passe par une sentinelle textuelle. */
const statutChoisi = computed({
    get: () => (filtres.statut === null ? 'tous' : String(filtres.statut)),
    set: (v: string) => { filtres.statut = v === 'tous' ? null : Number(v); getData() },
})

const étatChoisi = computed({
    get: () => filtres.etat ?? 'tous',
    set: (v: string) => { filtres.etat = v === 'tous' ? null : v; getData() },
})

/* ── Options des listes déroulantes ─────────────────────────────────── */

const optionsFournisseurs = computed(() =>
    [...catalogue.fournisseurs.entries()]
        .map(([value, title]) => ({ title, value }))
        .sort((a, b) => a.title.localeCompare(b.title, 'fr-CA')))

const suggestionsProduits = ref<string[]>([])
const isLoadingProduits = ref(false)

const optionsProduits = computed(() => {
    const map = new Map(catalogue.produits)
    for (const p of storeU.vegco?.produits ?? [])
        if (!map.has(p.codeProduit))
            map.set(p.codeProduit, `${p.codeProduit} — ${p.description}`)
    for (const code of suggestionsProduits.value)
        if (!map.has(code))
            map.set(code, code)
    return [...map.entries()]
        .map(([value, title]) => ({ title, value }))
        .sort((a, b) => a.title.localeCompare(b.title, 'fr-CA'))
})

let minuteurRechercheProduits: ReturnType<typeof setTimeout> | null = null
function rechercherProduits(phrase: string | null) {
    if (minuteurRechercheProduits) clearTimeout(minuteurRechercheProduits)
    if (!phrase || phrase.trim().length === 0) {
        suggestionsProduits.value = []
        return
    }
    minuteurRechercheProduits = setTimeout(async () => {
        isLoadingProduits.value = true
        try {
            const réponse = await apiProphet.get('vgco/produits/completion', { params: { phrase: phrase.trim() } })
            suggestionsProduits.value = réponse.data as string[]
        }
        catch (e) {
            notifyError(e)
        }
        finally {
            isLoadingProduits.value = false
        }
    }, 300)
}

const optionsLots = computed(() => [...catalogue.lots].sort().reverse())
const optionsCommandesAchat = computed(() => [...catalogue.commandesAchat].sort().reverse())
const optionsCommandesVente = computed(() => [...catalogue.commandesVente].sort().reverse())
const optionsClients = computed(() => [...catalogue.nomsClients].sort((a, b) => a.localeCompare(b, 'fr-CA')))
const optionsPoClient = computed(() => [...catalogue.poClients].sort())

/* ── Colonnes & tableau ─────────────────────────────────────────────── */

const colonnesVisibles = computed(() => COLONNES.filter(c => colonnesActives.value.includes(c.key)))

const headersTable = computed(() => colonnesVisibles.value.map(c => ({
    title: c.title,
    key: c.key,
    align: (c.align ?? 'start') as 'start' | 'end',
    width: c.width,
})))

const itemsParPageEffectif = computed(() => (groupBy.value.length > 0 ? -1 : itemsParPage.value))

watch(groupementChoisi, valeurs => {
    groupBy.value = valeurs.map(key => ({ key, order: 'asc' as const }))
    if (valeurs.length === 0)
        vue.value = 'detail'
    else if (vue.value === 'flux' && valeurs.length < 2)
        vue.value = 'sommaire'
})

/* ── Regroupement (niveaux ordonnés) ────────────────────────────────── */

const groupementDisponible = computed(() =>
    OPTIONS_GROUPEMENT.filter(o => !groupementChoisi.value.includes(o.value)))

function ajouterGroupement(clé: string) {
    if (!groupementChoisi.value.includes(clé))
        groupementChoisi.value = [...groupementChoisi.value, clé]
}

function retirerGroupement(index: number) {
    const copie = [...groupementChoisi.value]
    copie.splice(index, 1)
    groupementChoisi.value = copie
}

function déplacerGroupement(index: number, delta: number) {
    const cible = index + delta
    if (cible < 0 || cible >= groupementChoisi.value.length)
        return
    const copie = [...groupementChoisi.value]
    const [élément] = copie.splice(index, 1)
    copie.splice(cible, 0, élément!)
    groupementChoisi.value = copie
}

function viderGroupement() {
    groupementChoisi.value = []
}

function texteCellule(ligne: PaletteRow, col: ColPalette): string {
    switch (col.key) {
        case 'dateReception': return ligne.dateReceptionF
        case 'dateRecolte': return ligne.dateRecolteF
        case 'dateExpedition': return ligne.dateExpeditionF
        default: break
    }
    const valeur = (ligne as any)[col.key]
    if (col.type === 'nombre') return fmtNombre(valeur)
    if (col.type === 'poids') return fmtPoids(valeur)
    if (valeur === null || valeur === undefined || valeur === '' || valeur === '0') return '—'
    return String(valeur)
}

function couleurStatut(ligne: PaletteRow): string {
    return ligne.statut === 1 ? 'info' : 'success'
}

function couleurÉtat(ligne: PaletteRow): string {
    return ligne.idEtat === 'Q' ? 'warning' : 'success'
}

/* ── Groupes ────────────────────────────────────────────────────────── */

function lignesDuGroupe(groupe: any): PaletteRow[] {
    const sortie: PaletteRow[] = []
    const parcourir = (noeud: any) => {
        if (!noeud) return
        if (noeud.type === 'group')
            (noeud.items ?? []).forEach(parcourir)
        else if (noeud.raw)
            sortie.push(noeud.raw as PaletteRow)
    }
    parcourir(groupe)
    return sortie
}

function libelléValeurGroupe(clé: string, valeur: string): string {
    if (!valeur || valeur === '—' || valeur === '0')
        return '(aucun)'
    if (clé === 'jourReception' || clé === 'jourRecolte')
        return to_wd_dd_MMMM_yyyy(new Date(`${valeur}T00:00:00`))
    return valeur
}

function sommesGroupe(groupe: any) {
    const lignes = lignesDuGroupe(groupe)
    return {
        palettes: lignes.length,
        quantite: lignes.reduce((s, l) => s + (l.quantite || 0), 0),
        poidsNet: lignes.reduce((s, l) => s + l.poidsNet, 0),
    }
}

/* ── Hiérarchie visuelle des groupes ────────────────────────────────── */

/** Rang du groupe dans la hiérarchie choisie (0 = niveau principal). */
function niveauGroupe(groupe: any): number {
    const index = groupementChoisi.value.indexOf(groupe.key)
    return index >= 0 ? index : 0
}

function couleurNiveau(niveau: number): string {
    return COULEURS_FLUX[niveau % COULEURS_FLUX.length]!
}

function rgbaNiveau(niveau: number, alpha: number): string {
    const hex = couleurNiveau(niveau)
    const r = Number.parseInt(hex.slice(1, 3), 16)
    const g = Number.parseInt(hex.slice(3, 5), 16)
    const b = Number.parseInt(hex.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const INDENTATION_NIVEAU = 20
/** Largeur d'une bande du rail de hiérarchie, en pixels. */
const LARGEUR_BANDE = 4

/**
 * Rail vertical à gauche : une bande colorée par niveau de regroupement.
 * Rendu en `box-shadow: inset` — contrairement à `border-left` ou `padding-left`,
 * cela ne modifie pas la largeur des cellules et le tableau reste aligné.
 */
function railNiveaux(profondeur: number): string {
    const bandes: string[] = []
    // La première ombre est dessinée par-dessus les suivantes : niveaux du plus étroit au plus large.
    for (let i = 0; i < profondeur; i++)
        bandes.push(`inset ${(i + 1) * LARGEUR_BANDE}px 0 0 ${rgbaNiveau(i, 0.85)}`)
    return bandes.join(', ')
}

/** Chaque niveau a sa couleur et une teinte de fond de plus en plus légère. */
function styleGroupe(groupe: any) {
    return { background: rgbaNiveau(niveauGroupe(groupe), Math.max(0.05, 0.18 - niveauGroupe(groupe) * 0.04)) }
}

function styleCelluleGroupe(groupe: any) {
    return { boxShadow: railNiveaux(niveauGroupe(groupe) + 1) }
}

/** Retrait du contenu de l'entête de groupe (le `td` couvre toutes les colonnes : sans impact sur l'alignement). */
function styleContenuGroupe(groupe: any) {
    return { marginLeft: `${niveauGroupe(groupe) * INDENTATION_NIVEAU}px` }
}

function styleBadgeNiveau(niveau: number) {
    return {
        background: rgbaNiveau(niveau, 0.9),
        color: '#ffffff',
    }
}

/** Les lignes de palettes portent le rail complet de la hiérarchie. */
const styleLigneGroupée = computed(() => {
    const profondeur = groupementChoisi.value.length
    if (profondeur === 0)
        return undefined
    return { boxShadow: railNiveaux(profondeur) }
})

/** Largeur à imposer à la colonne de pliage ajoutée par Vuetify : juste de quoi loger le rail. */
const largeurColonneGroupe = computed(() => `${Math.max(1, groupementChoisi.value.length) * LARGEUR_BANDE}px`)

/* ── Vue sommaire (agrégats) ────────────────────────────────────────── */

const agrégats = computed((): Agrégat[] => {
    if (groupBy.value.length === 0)
        return []

    const map = new Map<string, { a: Agrégat, lots: Set<string>, produits: Set<string>, clients: Set<string>, fournisseurs: Set<string> }>()

    for (const l of lignesFiltrées.value) {
        const valeurs = groupBy.value.map(g => libelléValeurGroupe(g.key, String((l as any)[g.key] ?? '')))
        const clé = valeurs.join(' ⁄ ')

        let entrée = map.get(clé)
        if (!entrée) {
            entrée = {
                a: { clé, valeurs, palettes: 0, quantite: 0, quantiteInventaire: 0, poidsNet: 0, poidsBrut: 0, lots: 0, produits: 0, clients: 0, fournisseurs: 0 },
                lots: new Set(), produits: new Set(), clients: new Set(), fournisseurs: new Set(),
            }
            map.set(clé, entrée)
        }

        entrée.a.palettes++
        entrée.a.quantite += l.quantite || 0
        entrée.a.quantiteInventaire += l.quantiteInventaire || 0
        entrée.a.poidsNet += l.poidsNet
        entrée.a.poidsBrut += l.poidsBrut || 0
        if (l.noLot) entrée.lots.add(l.noLot)
        if (l.codeProduit) entrée.produits.add(l.codeProduit)
        if (l.client && l.client !== '—') entrée.clients.add(l.client)
        if (l.fournisseur) entrée.fournisseurs.add(l.fournisseur)
    }

    return [...map.values()]
        .map(e => ({
            ...e.a,
            lots: e.lots.size,
            produits: e.produits.size,
            clients: e.clients.size,
            fournisseurs: e.fournisseurs.size,
        }))
        .sort((a, b) => b.quantite - a.quantite)
})

/**
 * Arbre des regroupements : chaque niveau porte ses propres sous-totaux, contrairement à la
 * vue plate qui n'agrège que les combinaisons feuilles.
 */
declare interface NoeudSommaire {
    clé: string
    niveau: number
    cléGroupe: string
    libellé: string
    palettes: number
    quantite: number
    quantiteInventaire: number
    poidsNet: number
    lots: number
    produits: number
    clients: number
    enfants: NoeudSommaire[]
}

declare interface AccumulateurSommaire {
    clé: string
    niveau: number
    cléGroupe: string
    libellé: string
    palettes: number
    quantite: number
    quantiteInventaire: number
    poidsNet: number
    lots: Set<string>
    produits: Set<string>
    clients: Set<string>
    enfants: Map<string, AccumulateurSommaire>
}

const arbreSommaire = computed((): NoeudSommaire[] => {
    if (groupementChoisi.value.length === 0)
        return []

    const racine = new Map<string, AccumulateurSommaire>()

    for (const l of lignesFiltrées.value) {
        let niveauCourant = racine
        let cléParent = ''

        groupementChoisi.value.forEach((cléGroupe, i) => {
            const libellé = libelléValeurGroupe(cléGroupe, String((l as any)[cléGroupe] ?? ''))
            const clé = `${cléParent}⁄${cléGroupe}=${libellé}`

            let noeud = niveauCourant.get(libellé)
            if (!noeud) {
                noeud = {
                    clé, niveau: i, cléGroupe, libellé,
                    palettes: 0, quantite: 0, quantiteInventaire: 0, poidsNet: 0,
                    lots: new Set(), produits: new Set(), clients: new Set(),
                    enfants: new Map(),
                }
                niveauCourant.set(libellé, noeud)
            }

            noeud.palettes++
            noeud.quantite += l.quantite || 0
            noeud.quantiteInventaire += l.quantiteInventaire || 0
            noeud.poidsNet += l.poidsNet
            if (l.noLot) noeud.lots.add(l.noLot)
            if (l.codeProduit) noeud.produits.add(l.codeProduit)
            if (l.client && l.client !== '—') noeud.clients.add(l.client)

            cléParent = clé
            niveauCourant = noeud.enfants
        })
    }

    const convertir = (map: Map<string, AccumulateurSommaire>): NoeudSommaire[] =>
        [...map.values()]
            .sort((a, b) => b.quantite - a.quantite)
            .map(n => ({
                clé: n.clé,
                niveau: n.niveau,
                cléGroupe: n.cléGroupe,
                libellé: n.libellé,
                palettes: n.palettes,
                quantite: n.quantite,
                quantiteInventaire: n.quantiteInventaire,
                poidsNet: n.poidsNet,
                lots: n.lots.size,
                produits: n.produits.size,
                clients: n.clients.size,
                enfants: convertir(n.enfants),
            }))

    return convertir(racine)
})

/** Tout est déplié par défaut : on ne mémorise que les noeuds explicitement repliés. */
const sommaireReplié = ref(new Set<string>())

const lignesSommaire = computed(() => {
    const sortie: NoeudSommaire[] = []
    const parcourir = (noeuds: NoeudSommaire[]) => {
        for (const n of noeuds) {
            sortie.push(n)
            if (!sommaireReplié.value.has(n.clé))
                parcourir(n.enfants)
        }
    }
    parcourir(arbreSommaire.value)
    return sortie
})

function basculerNoeudSommaire(noeud: NoeudSommaire) {
    const repliés = new Set(sommaireReplié.value)
    if (!repliés.delete(noeud.clé))
        repliés.add(noeud.clé)
    sommaireReplié.value = repliés
}

function déplierToutSommaire() {
    sommaireReplié.value = new Set()
}

function replierToutSommaire() {
    const repliés = new Set<string>()
    const parcourir = (noeuds: NoeudSommaire[]) => {
        for (const n of noeuds) {
            if (n.enfants.length > 0) {
                repliés.add(n.clé)
                parcourir(n.enfants)
            }
        }
    }
    parcourir(arbreSommaire.value)
    sommaireReplié.value = repliés
}

function styleLigneSommaire(noeud: NoeudSommaire) {
    return { background: rgbaNiveau(noeud.niveau, Math.max(0.04, 0.16 - noeud.niveau * 0.04)) }
}

function styleCelluleSommaire(noeud: NoeudSommaire) {
    return {
        boxShadow: railNiveaux(noeud.niveau + 1),
        paddingLeft: `${12 + noeud.niveau * INDENTATION_NIVEAU}px`,
    }
}

/* ── Impression du sommaire ─────────────────────────────────────────── */

const dateImpression = ref(new Date())

const contexteImpression = computed(() => {
    const parts = [
        ...filtresAPIActifs.value.map(f => f.libellé),
        ...filtresLocaux.value.map(f => (f.exclure ? `≠ ${f.libellé}` : f.libellé)),
    ]
    return parts.length > 0 ? parts.join('  ·  ') : 'Aucun filtre actif'
})

async function imprimerSommaire() {
    dateImpression.value = new Date()
    // L'entête d'impression n'existe qu'une fois la date à jour rendue : sans ce délai, la
    // boîte d'impression fige la page avant que le DOM ne soit remis à jour.
    await nextTick()
    window.print()
}

/* ── Vue flux (diagramme de Sankey) ─────────────────────────────────── */

/** Au-delà, le diagramme devient illisible : les valeurs restantes sont fusionnées dans « Autres ». */
const MAX_NOEUDS_PAR_NIVEAU = 15
/** Au-delà, les colonnes deviennent trop étroites pour être lisibles. */
const MAX_NIVEAUX_FLUX = 4

const métriqueFlux = ref<'quantite' | 'poidsNet'>('quantite')

const niveauxFlux = computed(() => groupementChoisi.value.slice(0, MAX_NIVEAUX_FLUX))
const fluxDisponible = computed(() => niveauxFlux.value.length >= 2)

declare interface NoeudFlux {
    name: string
    niveau: number
    libellé: string
    brut: string
    valeur: number
    itemStyle: { color: string }
}

declare interface LienFlux {
    source: string
    target: string
    value: number
    libelléSource: string
    libelléCible: string
}

const COULEURS_FLUX = ['#1e88e5', '#43a047', '#fb8c00', '#8e24aa', '#00acc1', '#e53935']

const fluxSankey = computed(() => {
    const niveaux = niveauxFlux.value
    const vide = { noeuds: [] as NoeudFlux[], liens: [] as LienFlux[], tronqué: false, maxParNiveau: 0 }
    if (niveaux.length < 2)
        return vide

    const lignes = lignesFiltrées.value
    if (lignes.length === 0)
        return vide

    const mesure = (l: PaletteRow) => (métriqueFlux.value === 'quantite' ? (l.quantite || 0) : l.poidsNet)

    // 1er passage : totaux par valeur pour chaque niveau, afin de ne garder que les plus importantes.
    const totauxNiveau = niveaux.map(() => new Map<string, number>())
    for (const l of lignes) {
        niveaux.forEach((clé, i) => {
            const lib = libelléValeurGroupe(clé, String((l as any)[clé] ?? ''))
            totauxNiveau[i]!.set(lib, (totauxNiveau[i]!.get(lib) ?? 0) + mesure(l))
        })
    }

    let tronqué = false
    const retenus = totauxNiveau.map(m => {
        const triés = [...m.entries()].sort((a, b) => b[1] - a[1])
        if (triés.length > MAX_NOEUDS_PAR_NIVEAU)
            tronqué = true
        return new Set(triés.slice(0, MAX_NOEUDS_PAR_NIVEAU).map(([k]) => k))
    })
    const libelléAutres = totauxNiveau.map((m, i) => `Autres (${m.size - retenus[i]!.size})`)

    // 2e passage : construction des nœuds et des liens.
    // Les noms de nœuds sont préfixés par leur niveau, sinon une même valeur présente à deux
    // niveaux créerait un cycle et ECharts refuserait de dessiner le diagramme.
    const noeuds = new Map<string, NoeudFlux>()
    const liens = new Map<string, LienFlux>()

    for (const l of lignes) {
        const v = mesure(l)
        const ids: string[] = []
        const libellés: string[] = []

        niveaux.forEach((clé, i) => {
            const brut = String((l as any)[clé] ?? '')
            const affiché = libelléValeurGroupe(clé, brut)
            const libellé = retenus[i]!.has(affiché) ? affiché : libelléAutres[i]!
            const id = `${i}\u0000${libellé}`
            const existant = noeuds.get(id)
            if (existant)
                existant.valeur += v
            else
                noeuds.set(id, {
                    name: id,
                    niveau: i,
                    libellé,
                    brut: libellé === affiché ? brut : '',
                    valeur: v,
                    itemStyle: { color: COULEURS_FLUX[i % COULEURS_FLUX.length]! },
                })
            ids.push(id)
            libellés.push(libellé)
        })

        for (let i = 0; i < ids.length - 1; i++) {
            const clé = `${ids[i]}\u0001${ids[i + 1]}`
            const lien = liens.get(clé)
            if (lien)
                lien.value += v
            else
                liens.set(clé, {
                    source: ids[i]!,
                    target: ids[i + 1]!,
                    value: v,
                    libelléSource: libellés[i]!,
                    libelléCible: libellés[i + 1]!,
                })
        }
    }

    const compteParNiveau = niveaux.map((_, i) => [...noeuds.values()].filter(n => n.niveau === i).length)

    return {
        noeuds: [...noeuds.values()].filter(n => n.valeur > 0),
        liens: [...liens.values()].filter(l => l.value > 0),
        tronqué,
        maxParNiveau: Math.max(0, ...compteParNiveau),
    }
})

const totalFlux = computed(() => fluxSankey.value.noeuds
    .filter(n => n.niveau === 0)
    .reduce((s, n) => s + n.valeur, 0))

const unitéFlux = computed(() => (métriqueFlux.value === 'quantite' ? 'unités' : 'lb'))

function fmtMesureFlux(v: number): string {
    return métriqueFlux.value === 'quantite' ? fmtNombre(v) : fmtPoids(v)
}

function partFlux(v: number): string {
    const total = totalFlux.value
    return total > 0 ? `${((v / total) * 100).toFixed(1)} %` : '—'
}

const hauteurFlux = computed(() => Math.max(420, fluxSankey.value.maxParNiveau * 32 + 80))

const optionFlux = computed((): ECOption => {
    const sombre = theme.global.name.value === 'dark'
    const couleurTexte = sombre ? '#e0e0e0' : '#333333'

    return {
        tooltip: {
            trigger: 'item',
            triggerOn: 'mousemove',
            confine: true,
            formatter: (params: any) => {
                if (params.dataType === 'edge') {
                    const d = params.data as LienFlux
                    return `<strong>${d.libelléSource}</strong> → <strong>${d.libelléCible}</strong><br/>`
                        + `${fmtMesureFlux(d.value)} ${unitéFlux.value} (${partFlux(d.value)})`
                }
                const d = params.data as NoeudFlux
                return `<strong>${d.libellé}</strong><br/>`
                    + `${fmtMesureFlux(d.valeur)} ${unitéFlux.value} (${partFlux(d.valeur)})`
            },
        },
        series: [{
            type: 'sankey',
            left: 12,
            right: 140,
            top: 16,
            bottom: 16,
            nodeWidth: 16,
            nodeGap: 10,
            nodeAlign: 'justify',
            draggable: false,
            emphasis: { focus: 'adjacency' },
            data: fluxSankey.value.noeuds,
            links: fluxSankey.value.liens,
            label: {
                color: couleurTexte,
                fontSize: 11,
                formatter: (params: any) => (params.data as NoeudFlux).libellé,
            },
            lineStyle: { color: 'gradient', opacity: 0.4, curveness: 0.5 },
        }],
    }
})

/** Clic sur un nœud ou un lien : ajoute un filtre local pour explorer le flux. */
function surClicFlux(params: any) {
    const niveaux = niveauxFlux.value

    const filtrerNoeud = (n: NoeudFlux) => {
        if (!n || n.brut === '')
            return false
        const clé = niveaux[n.niveau]
        if (!clé)
            return false
        ajouterFiltreLocal(clé, LIBELLÉS_GROUPES[clé] ?? clé, n.brut, false)
        return true
    }

    if (params.dataType === 'edge') {
        const lien = params.data as LienFlux
        const source = fluxSankey.value.noeuds.find(n => n.name === lien.source)
        const cible = fluxSankey.value.noeuds.find(n => n.name === lien.target)
        const ok = [source, cible].filter(Boolean).map(n => filtrerNoeud(n!)).some(Boolean)
        if (!ok)
            Notify.error('Ce flux regroupe plusieurs valeurs — impossible de filtrer dessus.')
        return
    }

    if (params.dataType === 'node' && !filtrerNoeud(params.data as NoeudFlux))
        Notify.error('Ce nœud regroupe plusieurs valeurs — impossible de filtrer dessus.')
}

/* ── Menu contextuel (clic droit) ───────────────────────────────────── */

const menu = reactive({
    show: false,
    x: 0,
    y: 0,
    col: null as ColPalette | null,
    libellé: '',
    valeurAffichée: '',
    valeurFiltre: '',
})

function ouvrirMenu(e: MouseEvent, ligne: PaletteRow, col: ColPalette) {
    const valeurFiltre = String((ligne as any)[col.cléFiltre ?? col.key] ?? '')
    menu.x = e.clientX
    menu.y = e.clientY
    menu.col = col
    menu.libellé = col.title
    menu.valeurAffichée = texteCellule(ligne, col)
    menu.valeurFiltre = valeurFiltre
    menu.show = false
    nextTick(() => { menu.show = true })
}

function ouvrirMenuGroupe(e: MouseEvent, clé: string, valeur: string) {
    const col = COLONNES.find(c => c.key === clé)
    menu.x = e.clientX
    menu.y = e.clientY
    menu.col = col ?? { title: LIBELLÉS_GROUPES[clé] ?? clé, key: clé }
    menu.libellé = LIBELLÉS_GROUPES[clé] ?? clé
    menu.valeurAffichée = valeur
    menu.valeurFiltre = valeur
    menu.show = false
    nextTick(() => { menu.show = true })
}

function filtrerDepuisMenu() {
    if (!menu.col) return
    ajouterFiltre(menu.col, menu.valeurFiltre, menu.valeurAffichée)
    menu.show = false
}

function ajouterFiltre(col: ColPalette, valeurFiltre: string, valeurAffichée: string) {
    if (col.apiFiltre === 'statut') {
        const trouvé = STATUTS.find(s => s.title === valeurAffichée)
        filtres.statut = trouvé?.value ?? Number(valeurFiltre)
        getData()
        return
    }

    if (col.apiFiltre === 'etat') {
        filtres.etat = valeurFiltre || null
        getData()
        return
    }

    if (col.apiFiltre && valeurFiltre) {
        const liste = filtres[col.apiFiltre] as string[]
        if (!liste.includes(valeurFiltre)) {
            liste.push(valeurFiltre)
            getData()
        }
        return
    }

    // Pas de filtre API disponible pour cette colonne : filtre local.
    ajouterFiltreLocal(col.key, col.title, valeurAffichée, false)
}

function ajouterFiltreLocal(clé: string, libellé: string, valeur: string, exclure: boolean) {
    const existe = filtresLocaux.value.some(f => f.clé === clé && f.valeur === valeur && f.exclure === exclure)
    if (!existe)
        filtresLocaux.value.push({ clé, libellé, valeur, exclure })
}

function exclureDepuisMenu() {
    if (!menu.col) return
    ajouterFiltreLocal(menu.col.key, menu.col.title, menu.valeurAffichée, true)
    menu.show = false
}

function grouperDepuisMenu() {
    if (!menu.col) return
    const clé = OPTIONS_GROUPEMENT.some(o => o.value === menu.col!.key) ? menu.col.key : null
    if (!clé) {
        Notify.error('Ce champ ne peut pas servir de regroupement.')
        return
    }
    if (!groupementChoisi.value.includes(clé))
        groupementChoisi.value = [...groupementChoisi.value, clé]
    menu.show = false
}

const menuGroupable = computed(() => menu.col !== null && OPTIONS_GROUPEMENT.some(o => o.value === menu.col!.key))

async function copierDepuisMenu() {
    try {
        await navigator.clipboard.writeText(menu.valeurAffichée)
        Notify.success('Valeur copiée.')
    }
    catch {
        Notify.error('Impossible de copier la valeur.')
    }
    menu.show = false
}

/** Isole la valeur : retire tous les autres filtres et interroge l'API sans borne de date. */
function tracerDepuisMenu() {
    if (!menu.col?.apiFiltre || menu.col.apiFiltre === 'statut' || menu.col.apiFiltre === 'etat' || !menu.valeurFiltre) return
    const cible = menu.col.apiFiltre
    filtres.paletteIds = []
    filtres.noLots = []
    filtres.noFournisseur = []
    filtres.codesProduits = []
    filtres.noCommandesVente = []
    filtres.noCommandesAchat = []
    filtres.nomClient = []
    filtres.poClient = []
    filtres.statut = null
    filtres.etat = null
    filtres.dateMin = ''
    filtres.dateMax = ''
    filtres.dateRecolteMin = ''
    filtres.dateRecolteMax = ''
    filtres.dateExpeditionMin = ''
    filtres.dateExpeditionMax = ''
    filtresLocaux.value = []
    ;(filtres[cible] as string[]).push(menu.valeurFiltre)
    menu.show = false
    getData()
}

const menuTraçable = computed(() =>
    menu.col?.apiFiltre !== undefined && menu.col?.apiFiltre !== 'statut' && menu.col?.apiFiltre !== 'etat'
    && menu.valeurFiltre !== '' && menu.valeurFiltre !== '0')

/* ── Détail d'une palette ───────────────────────────────────────────── */

const dialogDétail = ref(false)
const paletteDétail = ref<PaletteRow | null>(null)

function ouvrirDétail(ligne: PaletteRow) {
    paletteDétail.value = ligne
    dialogDétail.value = true
}

function tracerDepuisDétail(cible: 'paletteIds' | 'noLots' | 'noCommandesAchat' | 'noCommandesVente' | 'codesProduits' | 'poClient', valeur: string) {
    if (!valeur || valeur === '0') return
    filtres.paletteIds = []
    filtres.noLots = []
    filtres.noFournisseur = []
    filtres.codesProduits = []
    filtres.noCommandesVente = []
    filtres.noCommandesAchat = []
    filtres.nomClient = []
    filtres.poClient = []
    filtres.statut = null
    filtres.etat = null
    filtres.dateMin = ''
    filtres.dateMax = ''
    filtres.dateRecolteMin = ''
    filtres.dateRecolteMax = ''
    filtres.dateExpeditionMin = ''
    filtres.dateExpeditionMax = ''
    filtresLocaux.value = []
    ;(filtres[cible] as string[]).push(valeur)
    dialogDétail.value = false
    getData()
}

const champsDétail = computed(() => {
    const p = paletteDétail.value
    if (!p) return []
    return [
        { libellé: 'ID palette', valeur: p.paletteId },
        { libellé: 'No lot', valeur: p.noLot || '—' },
        { libellé: 'No item', valeur: p.noItem || '—' },
        { libellé: 'Code produit', valeur: p.codeProduit || '—' },
        { libellé: 'Produit', valeur: p.produit },
        { libellé: 'Fournisseur', valeur: `${p.noFournisseur || '—'} — ${p.nomFournisseur || '—'}` },
        { libellé: 'Client', valeur: p.noClient ? `${p.noClient} — ${p.nomClient}` : '—' },
        { libellé: 'Commande d\'achat', valeur: p.noCommandeAchat || '—' },
        { libellé: 'Commande de vente', valeur: p.noCommandeVente && p.noCommandeVente !== '0' ? p.noCommandeVente : '—' },
        { libellé: 'PO client', valeur: p.poClient || '—' },
        { libellé: 'Statut', valeur: p.statutLibellé },
        { libellé: 'État', valeur: p.étatLibellé },
        { libellé: 'Date de réception', valeur: p.dateReceptionF },
        { libellé: 'Date de récolte', valeur: p.dateRecolteF },
        { libellé: 'Date d\'expédition', valeur: p.dateExpeditionF },
        { libellé: 'Unités', valeur: fmtNombre(p.quantite) },
        { libellé: 'Unités en inventaire', valeur: fmtNombre(p.quantiteInventaire) },
        { libellé: 'Poids brut', valeur: `${fmtPoids(p.poidsBrut)} lb` },
        { libellé: 'Poids palette', valeur: `${fmtPoids(p.poidsPalette)} lb` },
        { libellé: 'Poids contenant (unité)', valeur: `${fmtPoids(p.poidsContenant)} lb` },
        { libellé: 'Poids net estimé', valeur: `${fmtPoids(p.poidsNet)} lb` },
    ]
})

/* ── Exportation Excel ──────────────────────────────────────────────── */

function exporterExcel() {
    try {
        const lignes = lignesFiltrées.value
        if (lignes.length === 0) {
            Notify.error('Aucune donnée à exporter.')
            return
        }

        const détail = lignes.map(l => {
            const ligne: Record<string, string | number> = {}
            for (const g of groupBy.value)
                ligne[LIBELLÉS_GROUPES[g.key] ?? g.key] = libelléValeurGroupe(g.key, String((l as any)[g.key] ?? ''))
            for (const c of colonnesVisibles.value) {
                if (c.type === 'nombre' || c.type === 'poids')
                    ligne[c.title] = Number(((l as any)[c.key] ?? 0).toFixed(2))
                else
                    ligne[c.title] = texteCellule(l, c)
            }
            ligne['Anomalie de poids'] = anomaliePoids(l)?.libellé ?? ''
            return ligne
        })

        const classeur = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(classeur, XLSX.utils.json_to_sheet(détail), 'Palettes')

        if (agrégats.value.length > 0) {
            const sommaire = agrégats.value.map(a => {
                const ligne: Record<string, string | number> = {}
                groupBy.value.forEach((g, i) => { ligne[LIBELLÉS_GROUPES[g.key] ?? g.key] = a.valeurs[i] })
                ligne['Palettes'] = a.palettes
                ligne['Unités'] = a.quantite
                ligne['Unités inv.'] = a.quantiteInventaire
                ligne['Poids net (lb)'] = Number(a.poidsNet.toFixed(2))
                ligne['Lots'] = a.lots
                ligne['Produits'] = a.produits
                ligne['Clients'] = a.clients
                return ligne
            })
            XLSX.utils.book_append_sheet(classeur, XLSX.utils.json_to_sheet(sommaire), 'Sommaire')
        }

        const horodatage = new Date()
        const nom = `palettes_${isoJour(horodatage)}_${String(horodatage.getHours()).padStart(2, '0')}${String(horodatage.getMinutes()).padStart(2, '0')}.xlsx`
        XLSX.writeFile(classeur, nom)
        Notify.success(`${lignes.length} palettes exportées.`)
    }
    catch (e) {
        notifyError(e)
    }
}

/* ── Partage de la vue par URL ──────────────────────────────────────── */

const route = useRoute()
const router = useRouter()

type CléDate = 'dateMin' | 'dateMax' | 'dateRecolteMin' | 'dateRecolteMax' | 'dateExpeditionMin' | 'dateExpeditionMax'
type CléListe = 'paletteIds' | 'noLots' | 'noFournisseur' | 'codesProduits' | 'nomClient' | 'poClient' | 'noCommandesAchat' | 'noCommandesVente'

/** Noms d'URL courts : le lien partagé doit rester lisible à l'œil nu. */
const DATES_URL: Record<string, CléDate> = {
    du: 'dateMin',
    au: 'dateMax',
    recolteDu: 'dateRecolteMin',
    recolteAu: 'dateRecolteMax',
    expedieDu: 'dateExpeditionMin',
    expedieAu: 'dateExpeditionMax',
}

const LISTES_URL: Record<string, CléListe> = {
    palette: 'paletteIds',
    lot: 'noLots',
    fournisseur: 'noFournisseur',
    produit: 'codesProduits',
    client: 'nomClient',
    poClient: 'poClient',
    cmdAchat: 'noCommandesAchat',
    cmdVente: 'noCommandesVente',
}

const CLÉS_URL = [
    ...Object.keys(DATES_URL), ...Object.keys(LISTES_URL),
    'statut', 'etat', 'q', 'local', 'groupe', 'vue', 'mesure',
]

/** Seuls les réglages qui s'écartent du défaut sont écrits : l'URL reste courte. */
const requêteURL = computed(() => {
    const q: Record<string, string | string[]> = {}

    for (const [nom, champ] of Object.entries(DATES_URL))
        if (filtres[champ])
            q[nom] = filtres[champ]

    for (const [nom, champ] of Object.entries(LISTES_URL))
        if (filtres[champ].length > 0)
            q[nom] = [...filtres[champ]]

    if (filtres.statut !== null)
        q.statut = String(filtres.statut)
    if (filtres.etat)
        q.etat = filtres.etat
    if (recherche.value.trim())
        q.q = recherche.value.trim()
    // Une valeur pouvant contenir n'importe quel caractère, le `!` d'exclusion est mis en préfixe
    // et la clé séparée par le premier `:` seulement.
    if (filtresLocaux.value.length > 0)
        q.local = filtresLocaux.value.map(f => `${f.exclure ? '!' : ''}${f.clé}:${f.valeur}`)
    if (groupementChoisi.value.length > 0)
        q.groupe = [...groupementChoisi.value]
    if (vue.value !== 'detail')
        q.vue = vue.value
    if (vue.value === 'flux' && métriqueFlux.value !== 'quantite')
        q.mesure = métriqueFlux.value

    return q
})

function texteURL(v: unknown): string {
    return (Array.isArray(v) ? String(v[0] ?? '') : String(v ?? '')).trim()
}

function listeURL(v: unknown): string[] {
    return (Array.isArray(v) ? v : [v])
        .map(x => String(x ?? '').trim())
        .filter(Boolean)
}

function décoderFiltreLocal(texte: string): FiltreLocal | null {
    const exclure = texte.startsWith('!')
    const reste = exclure ? texte.slice(1) : texte
    const séparateur = reste.indexOf(':')
    if (séparateur <= 0)
        return null
    const clé = reste.slice(0, séparateur)
    return {
        clé,
        libellé: COLONNES.find(c => c.key === clé)?.title ?? LIBELLÉS_GROUPES[clé] ?? clé,
        valeur: reste.slice(séparateur + 1),
        exclure,
    }
}

function appliquerURL(q: Record<string, unknown>) {
    for (const [nom, champ] of Object.entries(DATES_URL))
        filtres[champ] = texteURL(q[nom])

    for (const [nom, champ] of Object.entries(LISTES_URL))
        filtres[champ] = listeURL(q[nom])

    const statut = Number(texteURL(q.statut))
    filtres.statut = q.statut === undefined || Number.isNaN(statut) ? null : statut
    filtres.etat = texteURL(q.etat) || null

    recherche.value = texteURL(q.q)
    filtresLocaux.value = listeURL(q.local)
        .map(décoderFiltreLocal)
        .filter((f): f is FiltreLocal => f !== null)

    groupementChoisi.value = listeURL(q.groupe)
        .filter(clé => OPTIONS_GROUPEMENT.some(o => o.value === clé))

    const vueURL = texteURL(q.vue)
    vue.value = vueURL === 'sommaire' || vueURL === 'flux' ? vueURL : 'detail'
    métriqueFlux.value = texteURL(q.mesure) === 'poidsNet' ? 'poidsNet' : 'quantite'
}

/** Même forme que `requêteURL` (clés courtes) : `appliquerURL` sait déjà la relire. */
const FILTRES_STORAGE_KEY = 'vgco.palettes.filtres'

function chargerFiltresSauvegardés(): Record<string, unknown> | null {
    try {
        const brut = localStorage.getItem(FILTRES_STORAGE_KEY)
        return brut ? JSON.parse(brut) : null
    }
    catch {
        return null
    }
}

// L'URL suit l'état en continu : le bouton Partager n'a plus qu'à copier la barre d'adresse.
// Le même état est aussi gardé en local : à la réouverture de la page (sans lien partagé),
// les filtres et regroupements retrouvés sont ceux laissés la dernière fois.
watch(() => JSON.stringify(requêteURL.value), (json) => {
    router.replace({ query: requêteURL.value })
    localStorage.setItem(FILTRES_STORAGE_KEY, json)
})

async function partagerVue() {
    try {
        await navigator.clipboard.writeText(window.location.href)
        Notify.success('Lien copié — il rouvre la page avec les mêmes filtres.')
    }
    catch {
        Notify.error('Le navigateur a refusé l\'accès au presse-papiers.')
    }
}

/* ── Cycle de vie ───────────────────────────────────────────────────── */

onMounted(() => {
    if (CLÉS_URL.some(clé => route.query[clé] !== undefined)) {
        appliquerURL(route.query)
        getData()
        return
    }

    const sauvegarde = chargerFiltresSauvegardés()
    if (sauvegarde) {
        appliquerURL(sauvegarde)
        getData()
    }
    else {
        appliquerPériode(7)
    }
})
</script>

<template>
    <v-card class="mx-4 my-4 page-card" rounded="xl" :loading="isLoading"
        style="height: 90vh; display: flex; flex-direction: column;">

        <!-- Page header -->
        <div class="page-header px-5 pt-5 pb-3">
            <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">

                <!-- Left: title -->
                <div class="d-flex align-center" style="gap: 12px;">
                    <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
                        <v-icon icon="mdi-forklift" size="26"/>
                    </v-avatar>
                    <div>
                        <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Suivi des palettes</div>
                        <div class="text-caption text-medium-emphasis d-none d-sm-block">
                            Chaque palette, de la réception à l'expédition — lots, commandes et poids
                        </div>
                    </div>
                </div>

                <!-- Right: actions -->
                <div class="d-flex align-center flex-wrap page-actions" style="gap: 8px;">
                    <!-- Raccourci pour les producteurs : filtre sur leur propre ferme -->
                    <v-btn v-if="fournisseurUtilisateur" :color="mesPalettesActif ? 'primary' : undefined"
                        :variant="mesPalettesActif ? 'flat' : 'tonal'" rounded="pill"
                        prepend-icon="mdi-account-cowboy-hat" @click="basculerMesPalettes">
                        <span class="d-none d-sm-inline">Mes palettes</span>
                        <v-tooltip activator="parent" location="bottom">
                            {{ mesPalettesActif
                                ? 'Retirer le filtre sur votre ferme'
                                : `Ne montrer que les palettes de ${storeU.siteSélectionné.nom}` }}
                        </v-tooltip>
                    </v-btn>

                    <v-text-field v-model="recherche" label="Recherche rapide" density="compact" variant="outlined"
                        rounded="lg" hide-details clearable prepend-inner-icon="mdi-magnify" autocomplete="off"
                        class="recherche-rapide" style="min-width: 220px; max-width: 260px;"/>

                    <v-btn :color="afficherFiltres ? 'primary' : undefined"
                        :variant="afficherFiltres ? 'tonal' : 'text'" rounded="pill"
                        prepend-icon="mdi-magnify"
                        :append-icon="smAndDown ? undefined : (afficherFiltres ? 'mdi-chevron-up' : 'mdi-chevron-down')"
                        aria-label="Filtres" @click="afficherFiltres = !afficherFiltres">
                        <span class="d-none d-sm-inline">Filtres</span>
                        <template v-if="filtresAPIActifs.length + filtresLocaux.length > 0">
                            &nbsp;({{ filtresAPIActifs.length + filtresLocaux.length }})
                        </template>
                    </v-btn>

                    <!-- Sélecteur de niveaux de regroupement -->
                    <v-menu :close-on-content-click="false" location="bottom start">
                        <template #activator="{ props }">
                            <v-btn v-bind="props"
                                :color="groupementChoisi.length > 0 ? 'primary' : undefined"
                                :variant="groupementChoisi.length > 0 ? 'tonal' : 'text'"
                                rounded="pill" prepend-icon="mdi-format-list-group">
                                <span class="d-none d-sm-inline">Grouper par</span>
                                <span v-if="groupementChoisi.length > 0" class="ms-1">
                                    ({{ groupementChoisi.length }})
                                </span>
                            </v-btn>
                        </template>

                        <v-card rounded="lg" min-width="360" max-width="440">
                            <div class="px-4 pt-3 pb-2">
                                <div class="text-subtitle-2 font-weight-bold">Niveaux de regroupement</div>
                                <div class="text-caption text-medium-emphasis">
                                    L'ordre compte&nbsp;: le niveau&nbsp;1 est le regroupement principal,
                                    les suivants le subdivisent. Utilisez les flèches pour le changer.
                                </div>
                            </div>

                            <v-divider/>

                            <v-list v-if="groupementChoisi.length > 0" density="compact">
                                <v-list-item v-for="(clé, i) in groupementChoisi" :key="clé">
                                    <template #prepend>
                                        <v-avatar size="24" color="primary" variant="tonal"
                                            class="me-3 text-caption font-weight-bold">
                                            {{ i + 1 }}
                                        </v-avatar>
                                    </template>
                                    <v-list-item-title>{{ LIBELLÉS_GROUPES[clé] ?? clé }}</v-list-item-title>
                                    <template #append>
                                        <v-btn icon="mdi-arrow-up" size="x-small" variant="text" :disabled="i === 0"
                                            @click="déplacerGroupement(i, -1)"/>
                                        <v-btn icon="mdi-arrow-down" size="x-small" variant="text"
                                            :disabled="i === groupementChoisi.length - 1"
                                            @click="déplacerGroupement(i, 1)"/>
                                        <v-btn icon="mdi-close" size="x-small" variant="text" color="error"
                                            @click="retirerGroupement(i)"/>
                                    </template>
                                </v-list-item>
                            </v-list>
                            <div v-else class="px-4 py-3 text-caption text-medium-emphasis">
                                Aucun regroupement — le tableau liste les palettes une à une.
                            </div>

                            <v-divider/>

                            <div class="px-4 pt-2 text-caption text-medium-emphasis">Ajouter un niveau</div>
                            <v-list density="compact" max-height="260" class="overflow-y-auto">
                                <v-list-item v-for="o in groupementDisponible" :key="o.value" :title="o.title"
                                    prepend-icon="mdi-plus" @click="ajouterGroupement(o.value)"/>
                                <v-list-item v-if="groupementDisponible.length === 0"
                                    title="Tous les champs sont déjà utilisés" disabled/>
                            </v-list>

                            <v-divider/>

                            <v-card-actions>
                                <v-btn variant="text" size="small" prepend-icon="mdi-close-circle-outline"
                                    :disabled="groupementChoisi.length === 0" @click="viderGroupement">
                                    Tout retirer
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-menu>

                    <!-- Sur mobile, le bandeau de totaux est replié par défaut pour laisser toute la place au tableau -->
                    <v-btn v-if="smAndDown" :color="afficherStatsMobile ? 'primary' : undefined"
                        :variant="afficherStatsMobile ? 'tonal' : 'text'" rounded="pill"
                        prepend-icon="mdi-chart-box-outline" aria-label="Totaux"
                        @click="afficherStatsMobile = !afficherStatsMobile">
                        <span class="d-none d-sm-inline">Totaux</span>
                    </v-btn>

                    <v-menu :close-on-content-click="false">
                        <template #activator="{ props }">
                            <v-btn v-bind="props" variant="text" rounded="pill" prepend-icon="mdi-view-column-outline">
                                <span class="d-none d-sm-inline">Colonnes</span>
                            </v-btn>
                        </template>
                        <v-card rounded="lg" min-width="240">
                            <v-list density="compact">
                                <v-list-item v-for="col in COLONNES" :key="col.key">
                                    <v-checkbox v-model="colonnesActives" :value="col.key" :label="col.title"
                                        density="compact" hide-details/>
                                </v-list-item>
                            </v-list>
                        </v-card>
                    </v-menu>

                    <v-btn variant="text" rounded="pill" prepend-icon="mdi-share-variant-outline"
                        aria-label="Partager la vue" @click="partagerVue">
                        <span class="d-none d-sm-inline">Partager</span>
                        <v-tooltip activator="parent" location="bottom" max-width="260">
                            Copie un lien qui rouvre la page avec exactement ces filtres,
                            ce regroupement et cette vue.
                        </v-tooltip>
                    </v-btn>

                    <!-- L'export n'a pas de sens sur mobile : le fichier n'y est généralement pas exploitable. -->
                    <v-btn v-if="!smAndDown" color="primary" variant="tonal" rounded="pill"
                        prepend-icon="mdi-microsoft-excel"
                        :disabled="lignesFiltrées.length === 0" @click="exporterExcel">
                        <span class="d-none d-sm-inline">Excel</span>
                    </v-btn>

                    <v-btn color="primary" variant="tonal" rounded="pill" prepend-icon="mdi-refresh"
                        :loading="isLoading" @click="getData()">
                        <span class="d-none d-sm-inline">Actualiser</span>
                    </v-btn>
                </div>
            </div>

            <!-- Regroupement + vue : masqué tant qu'aucun niveau n'est choisi, le bouton vit dans l'en-tête -->
            <div v-if="groupementChoisi.length > 0" class="d-flex align-center flex-wrap mt-3 groupement-bar"
                style="gap: 8px;">

                <!-- Fil d'Ariane des niveaux : rend l'ordre explicite -->
                <div class="d-flex align-center flex-wrap fil-groupement" style="gap: 4px;">
                    <template v-for="(clé, i) in groupementChoisi" :key="clé">
                        <v-icon v-if="i > 0" icon="mdi-chevron-right" size="16"
                            class="text-medium-emphasis"/>
                        <v-chip size="small" color="primary" variant="tonal" closable
                            @click:close="retirerGroupement(i)">
                            <span class="niveau-num me-1">{{ i + 1 }}</span>
                            {{ LIBELLÉS_GROUPES[clé] ?? clé }}
                        </v-chip>
                    </template>
                </div>

                <v-btn-toggle v-model="vue" mandatory density="compact"
                    variant="outlined" rounded="pill" divided>
                    <v-btn value="detail" size="small" prepend-icon="mdi-table">
                        <span class="d-none d-sm-inline">Détail</span>
                    </v-btn>
                    <v-btn value="sommaire" size="small" prepend-icon="mdi-sigma">
                        <span class="d-none d-sm-inline">Sommaire</span>
                    </v-btn>
                    <v-btn value="flux" size="small" prepend-icon="mdi-transit-connection-variant"
                        :disabled="!fluxDisponible">
                        <span class="d-none d-sm-inline">Flux</span>
                    </v-btn>
                </v-btn-toggle>

                <v-btn-toggle v-if="vue === 'flux'" v-model="métriqueFlux" mandatory density="compact"
                    variant="outlined" rounded="pill" divided>
                    <v-btn value="quantite" size="small" prepend-icon="mdi-cube-outline">
                        <span class="d-none d-sm-inline">Unités</span>
                    </v-btn>
                    <v-btn value="poidsNet" size="small" prepend-icon="mdi-weight-pound">
                        <span class="d-none d-sm-inline">Poids net</span>
                    </v-btn>
                </v-btn-toggle>
            </div>
        </div>

        <!-- Filtres — bureau : accordéon inline, ne masque jamais le tableau -->
        <template v-if="!smAndDown">
            <v-divider/>
            <v-expand-transition>
                <div v-show="afficherFiltres" class="filtres-zone px-5 py-3">

                    <!-- Grille libellé/contenu : les titres de section vivent dans la marge, pas sur leur propre ligne -->
                    <div class="filtres-grille">

                        <div class="filtre-libellé">
                            <v-icon icon="mdi-calendar-range-outline" size="15"/>
                            <span>Périodes</span>
                        </div>
                        <div class="filtre-contenu">
                            <v-date-input v-model="plageRéception" label="Réception" multiple="range"
                                density="compact" variant="outlined" rounded="lg" hide-details autocomplete="off"
                                class="filtre-champ" prepend-icon="" :picker-props="{ color: 'primary' }"
                                @update:model-value="getData()"/>
                            <v-date-input v-model="plageRécolte" label="Récolte" multiple="range"
                                density="compact" variant="outlined" rounded="lg" hide-details autocomplete="off"
                                class="filtre-champ" prepend-icon="" :picker-props="{ color: 'primary' }"
                                @update:model-value="getData()"/>
                            <v-date-input v-model="plageExpédition" label="Expédition" multiple="range"
                                density="compact" variant="outlined" rounded="lg" hide-details autocomplete="off"
                                class="filtre-champ" prepend-icon="" :picker-props="{ color: 'primary' }"
                                @update:model-value="getData()"/>
                            <div class="d-flex align-center" style="gap: 4px;">
                                <v-btn size="x-small" variant="tonal" rounded="pill"
                                    @click="appliquerPériode(1)">Aujourd'hui</v-btn>
                                <v-btn size="x-small" variant="tonal" rounded="pill"
                                    @click="appliquerPériode(7)">7 j</v-btn>
                                <v-btn size="x-small" variant="tonal" rounded="pill"
                                    @click="appliquerPériode(30)">30 j</v-btn>
                                <v-btn size="x-small" variant="text" rounded="pill"
                                    @click="appliquerPériode(null)">Sans date</v-btn>
                            </div>
                        </div>

                        <div class="filtre-libellé">
                            <v-icon icon="mdi-check-decagram-outline" size="15"/>
                            <span>Statut</span>
                        </div>
                        <!-- Deux listes de trois choix : plus lisibles en segments qu'en listes déroulantes -->
                        <div class="filtre-contenu">
                            <v-btn-toggle v-model="statutChoisi" mandatory density="compact" variant="outlined"
                                rounded="pill" divided>
                                <v-btn v-for="s in STATUTS" :key="String(s.value)"
                                    :value="s.value === null ? 'tous' : String(s.value)" size="small">
                                    {{ s.title }}
                                </v-btn>
                            </v-btn-toggle>
                            <v-btn-toggle v-model="étatChoisi" mandatory density="compact" variant="outlined"
                                rounded="pill" divided>
                                <v-btn v-for="e in ETATS" :key="String(e.value)" :value="e.value ?? 'tous'"
                                    size="small">
                                    {{ e.title }}
                                </v-btn>
                            </v-btn-toggle>
                        </div>

                        <div class="filtre-libellé">
                            <v-icon icon="mdi-account-group-outline" size="15"/>
                            <span>Partenaires</span>
                        </div>
                        <div class="filtre-contenu">
                            <v-autocomplete v-model="filtres.noFournisseur" :items="optionsFournisseurs"
                                label="Fournisseurs" multiple chips closable-chips density="compact"
                                variant="outlined" rounded="lg" hide-details class="filtre-champ"
                                autocomplete="off" @update:model-value="getData()"/>
                            <v-autocomplete v-model="filtres.codesProduits" :items="optionsProduits"
                                :loading="isLoadingProduits"
                                label="Codes de produits" multiple chips closable-chips density="compact"
                                variant="outlined" rounded="lg" hide-details class="filtre-champ"
                                autocomplete="off" @update:search="rechercherProduits"
                                @update:model-value="getData()"/>
                            <v-combobox v-model="filtres.nomClient" :items="optionsClients" label="Clients"
                                multiple chips closable-chips density="compact" variant="outlined" rounded="lg"
                                hide-details class="filtre-champ" autocomplete="off"
                                @update:model-value="getData()"/>
                        </div>

                        <div class="filtre-libellé">
                            <v-icon icon="mdi-barcode" size="15"/>
                            <span>Identifiants</span>
                        </div>
                        <div class="filtre-contenu">
                            <v-combobox v-model="filtres.paletteIds" :items="[]" label="ID de palettes"
                                multiple chips closable-chips density="compact" variant="outlined" rounded="lg"
                                hide-details class="filtre-champ" autocomplete="off"
                                :color="rechercheIdLocale ? 'warning' : undefined"
                                @update:model-value="getData()">
                                <template #append-inner>
                                    <v-icon icon="mdi-help-circle-outline" size="16" class="text-disabled"/>
                                    <v-tooltip activator="parent" location="top" max-width="280">
                                        {{ rechercheIdLocale
                                            ? 'ID partiel : la recherche ne porte que sur les palettes affichées.'
                                            : 'ID complet : recherche sur tout l\'historique. Collez une liste'
                                                + ' séparée par des virgules.' }}
                                    </v-tooltip>
                                </template>
                            </v-combobox>
                            <v-combobox v-model="filtres.noLots" :items="optionsLots" label="No de lots"
                                multiple chips closable-chips density="compact" variant="outlined" rounded="lg"
                                hide-details class="filtre-champ" autocomplete="off"
                                @update:model-value="getData()"/>
                            <v-combobox v-model="filtres.noCommandesAchat" :items="optionsCommandesAchat"
                                label="Commandes d'achat" multiple chips closable-chips density="compact"
                                variant="outlined" rounded="lg" hide-details class="filtre-champ"
                                autocomplete="off" @update:model-value="getData()"/>
                            <v-combobox v-model="filtres.noCommandesVente" :items="optionsCommandesVente"
                                label="Commandes de vente" multiple chips closable-chips density="compact"
                                variant="outlined" rounded="lg" hide-details class="filtre-champ"
                                autocomplete="off" @update:model-value="getData()"/>
                            <v-combobox v-model="filtres.poClient" :items="optionsPoClient" label="PO client"
                                multiple chips closable-chips density="compact" variant="outlined" rounded="lg"
                                hide-details class="filtre-champ" autocomplete="off"
                                @update:model-value="getData()"/>
                        </div>
                    </div>

                    <v-divider class="mt-3 mb-2"/>

                    <!-- Filtres actifs -->
                    <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                        <template v-if="filtresAPIActifs.length > 0 || filtresLocaux.length > 0">
                            <span class="text-caption text-medium-emphasis me-1">Filtres actifs&nbsp;:</span>
                            <v-chip v-for="f in filtresAPIActifs" :key="`api-${f.clé}`" size="small" color="primary"
                                variant="tonal" closable @click:close="f.retirer()">
                                {{ f.libellé }}
                            </v-chip>
                            <v-chip v-for="f in filtresLocaux" :key="`loc-${f.clé}|${f.valeur}|${f.exclure}`"
                                size="small" :color="f.exclure ? 'error' : 'secondary'" variant="tonal" closable
                                @click:close="filtresLocaux = filtresLocaux.filter(x => x !== f)">
                                {{ f.libellé }} {{ f.exclure ? '≠' : '=' }} {{ f.valeur }}
                            </v-chip>
                        </template>
                        <span v-else class="text-caption text-medium-emphasis">Aucun filtre actif.</span>
                        <v-spacer/>
                        <v-btn variant="text" size="small" rounded="pill"
                            prepend-icon="mdi-filter-remove-outline" @click="réinitialiser">
                            Réinitialiser les filtres
                        </v-btn>
                    </div>
                </div>
            </v-expand-transition>
        </template>

        <!-- Filtres — mobile : pop-up plein écran plutôt qu'accordéon, pour ne pas empiéter sur le tableau -->
        <v-dialog v-else v-model="afficherFiltres" fullscreen transition="dialog-bottom-transition">
            <v-card>
                <v-toolbar density="compact" color="primary">
                    <v-btn icon="mdi-close" @click="afficherFiltres = false"/>
                    <v-toolbar-title>Filtres</v-toolbar-title>
                    <v-spacer/>
                    <v-btn variant="text" prepend-icon="mdi-filter-remove-outline" @click="réinitialiser">
                        Réinitialiser
                    </v-btn>
                </v-toolbar>

                <v-card-text class="pa-4">

                    <!-- Périodes -->
                    <div class="filtre-groupe">
                        <div class="filtre-groupe__entete">
                            <v-icon icon="mdi-calendar-range-outline" size="15" class="text-medium-emphasis"/>
                            <span class="filtre-groupe__titre">Périodes</span>
                        </div>
                        <div class="d-flex align-center flex-wrap mb-2" style="gap: 6px;">
                            <v-btn size="x-small" variant="tonal" rounded="pill" @click="appliquerPériode(1)">Aujourd'hui</v-btn>
                            <v-btn size="x-small" variant="tonal" rounded="pill" @click="appliquerPériode(7)">7 j</v-btn>
                            <v-btn size="x-small" variant="tonal" rounded="pill" @click="appliquerPériode(30)">30 j</v-btn>
                            <v-btn size="x-small" variant="text" rounded="pill" @click="appliquerPériode(null)">Sans date</v-btn>
                        </div>
                        <v-date-input v-model="plageRéception" label="Réception" multiple="range"
                            density="compact" variant="outlined" rounded="lg" hide-details class="mb-2"
                            autocomplete="off" prepend-icon="" :picker-props="{ color: 'primary' }"
                            @update:model-value="getData()"/>
                        <v-date-input v-model="plageRécolte" label="Récolte" multiple="range"
                            density="compact" variant="outlined" rounded="lg" hide-details class="mb-2"
                            autocomplete="off" prepend-icon="" :picker-props="{ color: 'primary' }"
                            @update:model-value="getData()"/>
                        <v-date-input v-model="plageExpédition" label="Expédition" multiple="range"
                            density="compact" variant="outlined" rounded="lg" hide-details autocomplete="off"
                            prepend-icon="" :picker-props="{ color: 'primary' }" @update:model-value="getData()"/>
                    </div>

                    <!-- Statut & état -->
                    <div class="filtre-groupe">
                        <div class="filtre-groupe__entete">
                            <v-icon icon="mdi-check-decagram-outline" size="15" class="text-medium-emphasis"/>
                            <span class="filtre-groupe__titre">Statut & état</span>
                        </div>
                        <v-btn-toggle v-model="statutChoisi" mandatory density="compact" variant="outlined"
                            rounded="pill" divided class="mb-2 d-flex">
                            <v-btn v-for="s in STATUTS" :key="String(s.value)"
                                :value="s.value === null ? 'tous' : String(s.value)" size="small" class="flex-grow-1">
                                {{ s.title }}
                            </v-btn>
                        </v-btn-toggle>
                        <v-btn-toggle v-model="étatChoisi" mandatory density="compact" variant="outlined"
                            rounded="pill" divided class="d-flex">
                            <v-btn v-for="e in ETATS" :key="String(e.value)" :value="e.value ?? 'tous'" size="small"
                                class="flex-grow-1">
                                {{ e.title }}
                            </v-btn>
                        </v-btn-toggle>
                    </div>

                    <!-- Produits & partenaires -->
                    <div class="filtre-groupe">
                        <div class="filtre-groupe__entete">
                            <v-icon icon="mdi-account-group-outline" size="15" class="text-medium-emphasis"/>
                            <span class="filtre-groupe__titre">Produits & partenaires</span>
                        </div>
                        <v-row dense>
                            <v-col cols="12">
                                <v-autocomplete v-model="filtres.noFournisseur" :items="optionsFournisseurs"
                                    label="Fournisseurs" multiple chips closable-chips density="compact"
                                    variant="outlined" rounded="lg" hide-details autocomplete="off"
                                    :menu-props="menuPropsFiltresMobile"
                                    @update:model-value="getData()"/>
                            </v-col>
                            <v-col cols="12">
                                <v-autocomplete v-model="filtres.codesProduits" :items="optionsProduits"
                                    :loading="isLoadingProduits"
                                    label="Codes de produits" multiple chips closable-chips density="compact"
                                    variant="outlined" rounded="lg" hide-details autocomplete="off"
                                    :menu-props="menuPropsFiltresMobile"
                                    @update:search="rechercherProduits" @update:model-value="getData()"/>
                            </v-col>
                            <v-col cols="12">
                                <v-combobox v-model="filtres.nomClient" :items="optionsClients" label="Clients"
                                    multiple chips closable-chips density="compact" variant="outlined" rounded="lg"
                                    hide-details autocomplete="off" :menu-props="menuPropsFiltresMobile"
                                    @update:model-value="getData()"/>
                            </v-col>
                        </v-row>
                    </div>

                    <!-- Identifiants -->
                    <div class="filtre-groupe">
                        <div class="filtre-groupe__entete">
                            <v-icon icon="mdi-barcode" size="15" class="text-medium-emphasis"/>
                            <span class="filtre-groupe__titre">Identifiants</span>
                        </div>
                        <v-row dense>
                            <v-col cols="12">
                                <v-combobox v-model="filtres.paletteIds" :items="[]" label="ID de palettes"
                                    multiple chips closable-chips density="compact" variant="outlined" rounded="lg"
                                    persistent-hint autocomplete="off" :menu-props="menuPropsFiltresMobile"
                                    :messages="rechercheIdLocale
                                        ? 'Recherche partielle : uniquement dans les résultats affichés'
                                        : 'ID complet = recherche sur tout l\'historique · partiel accepté'"
                                    :color="rechercheIdLocale ? 'warning' : undefined"
                                    @update:model-value="getData()"/>
                            </v-col>
                            <v-col cols="12">
                                <v-combobox v-model="filtres.noLots" :items="optionsLots" label="No de lots"
                                    multiple chips closable-chips density="compact" variant="outlined" rounded="lg"
                                    hide-details autocomplete="off" :menu-props="menuPropsFiltresMobile"
                                    @update:model-value="getData()"/>
                            </v-col>
                            <v-col cols="12">
                                <v-combobox v-model="filtres.noCommandesAchat" :items="optionsCommandesAchat"
                                    label="Commandes d'achat" multiple chips closable-chips density="compact"
                                    variant="outlined" rounded="lg" hide-details autocomplete="off"
                                    :menu-props="menuPropsFiltresMobile"
                                    @update:model-value="getData()"/>
                            </v-col>
                            <v-col cols="12">
                                <v-combobox v-model="filtres.noCommandesVente" :items="optionsCommandesVente"
                                    label="Commandes de vente" multiple chips closable-chips density="compact"
                                    variant="outlined" rounded="lg" hide-details autocomplete="off"
                                    :menu-props="menuPropsFiltresMobile"
                                    @update:model-value="getData()"/>
                            </v-col>
                            <v-col cols="12">
                                <v-combobox v-model="filtres.poClient" :items="optionsPoClient" label="PO client"
                                    multiple chips closable-chips density="compact" variant="outlined" rounded="lg"
                                    hide-details autocomplete="off" :menu-props="menuPropsFiltresMobile"
                                    @update:model-value="getData()"/>
                            </v-col>
                        </v-row>
                    </div>

                    <!-- Filtres actifs -->
                    <div v-if="filtresAPIActifs.length > 0 || filtresLocaux.length > 0"
                        class="d-flex align-center flex-wrap mt-2" style="gap: 6px;">
                        <span class="text-caption text-medium-emphasis me-1">Filtres actifs&nbsp;:</span>
                        <v-chip v-for="f in filtresAPIActifs" :key="`api-${f.clé}`" size="small" color="primary"
                            variant="tonal" closable @click:close="f.retirer()">
                            {{ f.libellé }}
                        </v-chip>
                        <v-chip v-for="f in filtresLocaux" :key="`loc-${f.clé}|${f.valeur}|${f.exclure}`" size="small"
                            :color="f.exclure ? 'error' : 'secondary'" variant="tonal" closable
                            @click:close="filtresLocaux = filtresLocaux.filter(x => x !== f)">
                            {{ f.libellé }} {{ f.exclure ? '≠' : '=' }} {{ f.valeur }}
                        </v-chip>
                    </div>
                </v-card-text>

                <v-divider/>

                <v-card-actions class="pa-3">
                    <v-spacer/>
                    <v-btn color="primary" variant="flat" rounded="pill" @click="afficherFiltres = false">
                        Voir les résultats
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-divider/>

        <!-- Bandeau de totaux : facultatif sur mobile (bouton « Détails »), où l'espace vertical est précieux -->
        <v-expand-transition>
            <div v-if="!smAndDown || afficherStatsMobile" class="stats-bar px-5 py-2">
                <div class="stats-scroll d-flex align-center flex-wrap" style="gap: 18px;">
                    <div class="stat-bloc">
                        <div class="text-caption text-medium-emphasis">Palettes</div>
                        <div class="text-subtitle-1 font-weight-bold">{{ fmtNombre(totaux.palettes) }}</div>
                    </div>
                    <v-divider vertical class="d-none d-sm-block" style="height: 32px; align-self: center;"/>
                    <div class="stat-bloc">
                        <div class="text-caption text-medium-emphasis">Unités</div>
                        <div class="text-subtitle-1 font-weight-bold">{{ fmtNombre(totaux.quantite) }}</div>
                    </div>
                    <div class="stat-bloc">
                        <div class="text-caption text-medium-emphasis">Poids net</div>
                        <div class="text-subtitle-1 font-weight-bold">{{ fmtPoids(totaux.poidsNet) }} lb</div>
                    </div>
                    <v-divider vertical class="d-none d-sm-block" style="height: 32px; align-self: center;"/>
                    <div class="stat-bloc">
                        <div class="text-caption text-medium-emphasis">Lots</div>
                        <div class="text-subtitle-1 font-weight-bold">{{ fmtNombre(totaux.lots) }}</div>
                    </div>
                    <div class="stat-bloc">
                        <div class="text-caption text-medium-emphasis">Produits</div>
                        <div class="text-subtitle-1 font-weight-bold">{{ fmtNombre(totaux.produits) }}</div>
                    </div>
                    <div class="stat-bloc">
                        <div class="text-caption text-medium-emphasis">Fournisseurs</div>
                        <div class="text-subtitle-1 font-weight-bold">{{ fmtNombre(totaux.fournisseurs) }}</div>
                    </div>
                    <div class="stat-bloc">
                        <div class="text-caption text-medium-emphasis">Clients</div>
                        <div class="text-subtitle-1 font-weight-bold">{{ fmtNombre(totaux.clients) }}</div>
                    </div>
                </div>

                <v-alert v-if="limiteAtteinte" type="warning" variant="tonal" density="compact" class="py-1 mt-2"
                    icon="mdi-alert-outline">
                    Limite de {{ LIMITE_API }} palettes atteinte — précisez les filtres pour voir l'ensemble des données.
                </v-alert>
            </div>
        </v-expand-transition>

        <!-- Sur mobile, si le bandeau de totaux reste replié, on garde uniquement cette alerte, essentielle -->
        <div v-if="limiteAtteinte && smAndDown && !afficherStatsMobile" class="alerte-limite px-4 py-2">
            <v-alert type="warning" variant="tonal" density="compact" icon="mdi-alert-outline" class="text-body-2">
                Limite de {{ LIMITE_API }} palettes atteinte — précisez les filtres.
            </v-alert>
        </div>

        <v-divider/>

        <!-- Contenu -->
        <div class="contenu-vues" style="flex: 1 1 auto; overflow-x: auto; overflow-y: auto;">

            <!-- Vue sommaire : arbre des regroupements, déplié par défaut -->
            <div v-if="vue === 'sommaire' && groupementChoisi.length > 0" class="sommaire-zone">

                <div class="d-flex align-center flex-wrap px-4 py-2 sommaire-actions" style="gap: 8px;">
                    <v-btn size="small" variant="text" rounded="pill" prepend-icon="mdi-unfold-more-horizontal"
                        :disabled="sommaireReplié.size === 0" @click="déplierToutSommaire">
                        Tout déplier
                    </v-btn>
                    <v-btn size="small" variant="text" rounded="pill" prepend-icon="mdi-unfold-less-horizontal"
                        @click="replierToutSommaire">
                        Tout replier
                    </v-btn>
                    <v-spacer/>
                    <v-btn size="small" color="primary" variant="tonal" rounded="pill" prepend-icon="mdi-printer"
                        @click="imprimerSommaire">
                        Imprimer
                    </v-btn>
                </div>

                <!-- Visible seulement à l'impression : sans lui, la feuille sortie n'a ni titre ni contexte -->
                <div class="entete-impression px-4">
                    <div class="entete-impression__titre">Suivi des palettes — sommaire</div>
                    <div class="entete-impression__meta">
                        Édité le {{ to_wd_dd_MMMM_hh_mm(dateImpression) }} ·
                        {{ fmtNombre(totaux.palettes) }} palettes ·
                        Regroupement : {{ groupementChoisi.map(c => LIBELLÉS_GROUPES[c] ?? c).join(' › ') }}
                    </div>
                    <div class="entete-impression__meta">Filtres : {{ contexteImpression }}</div>
                </div>

                <table class="sommaire-tableau">
                    <thead>
                        <tr>
                            <th>Regroupement</th>
                            <th>Palettes</th>
                            <th>Unités</th>
                            <th>Unités inv.</th>
                            <th>Poids net (lb)</th>
                            <th>Lots</th>
                            <th>Produits</th>
                            <th>Clients</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="n in lignesSommaire" :key="n.clé" class="sommaire-ligne"
                            :class="`sommaire-niveau-${n.niveau}`"
                            :style="styleLigneSommaire(n)"
                            @contextmenu.prevent="ouvrirMenuGroupe($event, n.cléGroupe, n.libellé)">
                            <td class="sommaire-libellé" :style="styleCelluleSommaire(n)">
                                <v-btn v-if="n.enfants.length > 0" size="x-small" variant="text" class="me-1"
                                    :icon="sommaireReplié.has(n.clé) ? 'mdi-chevron-right' : 'mdi-chevron-down'"
                                    @click.stop="basculerNoeudSommaire(n)"/>
                                <span v-else class="sommaire-puce"/>
                                <span class="niveau-badge me-2" :style="styleBadgeNiveau(n.niveau)">
                                    {{ n.niveau + 1 }}
                                </span>
                                <span class="text-caption text-medium-emphasis me-2">
                                    {{ LIBELLÉS_GROUPES[n.cléGroupe] ?? n.cléGroupe }}
                                </span>
                                <span :class="n.niveau === 0 ? 'font-weight-bold' : 'font-weight-medium'">
                                    {{ n.libellé }}
                                </span>
                            </td>
                            <td class="text-end">{{ fmtNombre(n.palettes) }}</td>
                            <td class="text-end font-weight-bold">{{ fmtNombre(n.quantite) }}</td>
                            <td class="text-end">{{ fmtNombre(n.quantiteInventaire) }}</td>
                            <td class="text-end">{{ fmtPoids(n.poidsNet) }}</td>
                            <td class="text-end">{{ fmtNombre(n.lots) }}</td>
                            <td class="text-end">{{ fmtNombre(n.produits) }}</td>
                            <td class="text-end">{{ fmtNombre(n.clients) }}</td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr class="total-row">
                            <td class="font-weight-bold">Total</td>
                            <td class="text-end font-weight-bold">{{ fmtNombre(totaux.palettes) }}</td>
                            <td class="text-end font-weight-bold">{{ fmtNombre(totaux.quantite) }}</td>
                            <td class="text-end font-weight-bold">{{ fmtNombre(totaux.quantiteInventaire) }}</td>
                            <td class="text-end font-weight-bold">{{ fmtPoids(totaux.poidsNet) }}</td>
                            <td class="text-end font-weight-bold">{{ fmtNombre(totaux.lots) }}</td>
                            <td class="text-end font-weight-bold">{{ fmtNombre(totaux.produits) }}</td>
                            <td class="text-end font-weight-bold">{{ fmtNombre(totaux.clients) }}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            <!-- Vue flux (Sankey) -->
            <div v-else-if="vue === 'flux'" class="pa-4">
                <div class="d-flex align-center flex-wrap mb-2" style="gap: 8px;">
                    <v-chip v-for="(niveau, i) in niveauxFlux" :key="niveau" size="small" variant="tonal"
                        :color="COULEURS_FLUX[i % COULEURS_FLUX.length]">
                        {{ i + 1 }}. {{ LIBELLÉS_GROUPES[niveau] ?? niveau }}
                    </v-chip>
                    <span class="text-caption text-medium-emphasis">
                        Cliquez sur un flux ou une étape pour filtrer
                    </span>
                </div>

                <v-alert v-if="groupementChoisi.length > MAX_NIVEAUX_FLUX" type="info" variant="tonal"
                    density="compact" class="mb-2" icon="mdi-information-outline">
                    Seuls les {{ MAX_NIVEAUX_FLUX }} premiers regroupements sont représentés.
                </v-alert>
                <v-alert v-if="fluxSankey.tronqué" type="warning" variant="tonal" density="compact" class="mb-2"
                    icon="mdi-alert-outline">
                    Certaines étapes comptent plus de {{ MAX_NOEUDS_PAR_NIVEAU }} valeurs : les moins importantes
                    sont regroupées dans «&nbsp;Autres&nbsp;».
                </v-alert>

                <div v-if="fluxSankey.liens.length === 0" class="pa-8 text-center text-medium-emphasis">
                    <v-icon icon="mdi-transit-connection-variant" size="48" class="mb-2"/>
                    <div>Aucun flux à représenter pour ces filtres.</div>
                </div>
                <v-chart v-else :option="optionFlux" :style="{ height: `${hauteurFlux}px`, minWidth: '320px' }"
                    autoresize @click="surClicFlux"/>
            </div>

            <!-- Vue détail -->
            <v-data-table v-else :headers="headersTable" :items="lignesFiltrées" :loading="isLoading" item-value="uid"
                :group-by="groupBy" :items-per-page="itemsParPageEffectif"
                :items-per-page-options="[25, 50, 100, 250, 500, { value: -1, title: 'Tout' }]"
                :sort-by="[{ key: 'dateReception', order: 'desc' }]" density="compact" fixed-header
                :hide-default-header="smAndDown"
                :class="{ 'tableau-groupé': groupBy.length > 0 }"
                :style="{ '--largeur-colonne-groupe': largeurColonneGroupe }">

                <template #group-header="{ item, columns, toggleGroup, isGroupOpen }">
                    <tr class="groupe-row" :style="styleGroupe(item)">
                        <td :colspan="columns.length" :style="styleCelluleGroupe(item)">
                            <div class="d-flex align-center flex-wrap" style="gap: 8px;"
                                :style="styleContenuGroupe(item)"
                                @contextmenu.prevent="ouvrirMenuGroupe($event, item.key, libelléValeurGroupe(item.key, String(item.value ?? '')))">
                                <v-btn size="x-small" variant="text"
                                    :icon="isGroupOpen(item) ? 'mdi-chevron-down' : 'mdi-chevron-right'"
                                    @click="toggleGroup(item)"/>
                                <span class="niveau-badge" :style="styleBadgeNiveau(niveauGroupe(item))">
                                    {{ niveauGroupe(item) + 1 }}
                                </span>
                                <span class="text-caption text-medium-emphasis">
                                    {{ LIBELLÉS_GROUPES[item.key] ?? item.key }}
                                </span>
                                <span :class="niveauGroupe(item) === 0
                                    ? 'text-subtitle-2 font-weight-bold'
                                    : 'text-body-2 font-weight-bold'">
                                    {{ libelléValeurGroupe(item.key, String(item.value ?? '')) }}
                                </span>
                                <v-spacer/>
                                <v-chip size="x-small" variant="tonal" prepend-icon="mdi-package-variant-closed">
                                    {{ fmtNombre(sommesGroupe(item).palettes) }} palettes
                                </v-chip>
                                <v-chip size="x-small" color="primary" variant="tonal" prepend-icon="mdi-sigma">
                                    {{ fmtNombre(sommesGroupe(item).quantite) }} unités
                                </v-chip>
                                <v-chip size="x-small" color="secondary" variant="tonal" prepend-icon="mdi-weight-pound">
                                    {{ fmtPoids(sommesGroupe(item).poidsNet) }} lb
                                </v-chip>
                            </div>
                        </td>
                    </tr>
                </template>

                <template #item="{ item }">
                    <!-- Mobile : carte compacte -->
                    <tr v-if="smAndDown" class="palette-row" :class="classeLigne(item)" @click="ouvrirDétail(item)">
                        <td class="pt-2 pb-2" :colspan="groupBy.length > 0 ? 2 : 1" :style="styleLigneGroupée">
                            <div class="d-flex align-center flex-wrap mb-1" style="gap: 6px;">
                                <span class="font-weight-bold">{{ item.paletteId }}</span>
                                <v-chip size="x-small" :color="couleurStatut(item)" variant="tonal">
                                    {{ item.statutLibellé }}
                                </v-chip>
                                <v-chip v-if="item.idEtat === 'Q'" size="x-small" color="warning" variant="tonal">
                                    Quarantaine
                                </v-chip>
                                <v-chip v-if="anomalieAffichée(item)" size="x-small" variant="tonal"
                                    :color="anomalieAffichée(item)?.couleur" :prepend-icon="anomalieAffichée(item)?.icone">
                                    Poids douteux
                                </v-chip>
                            </div>
                            <div class="text-caption">Lot&nbsp;: {{ item.noLot || '—' }}</div>
                            <div class="text-caption">Produit&nbsp;: {{ item.codeProduit }} — {{ item.produit }}</div>
                            <div class="text-caption">Fournisseur&nbsp;: {{ item.fournisseur }}</div>
                            <div class="text-caption">Client&nbsp;: {{ item.client }}</div>
                            <div class="text-caption">Réception&nbsp;: {{ item.dateReceptionF }}</div>
                            <div class="text-caption">Expédition&nbsp;: {{ item.dateExpeditionF }}</div>
                            <div class="text-caption">
                                Unités&nbsp;: <strong>{{ fmtNombre(item.quantite) }}</strong> ·
                                Poids net&nbsp;: <strong>{{ fmtPoids(item.poidsNet) }} lb</strong>
                            </div>
                        </td>
                    </tr>

                    <!-- Bureau -->
                    <tr v-else class="palette-row" :class="classeLigne(item)" @click="ouvrirDétail(item)">
                        <!-- Vuetify ajoute une colonne d'entête pour le pliage des groupes : il faut la refléter ici. -->
                        <td v-if="groupBy.length > 0" class="td-groupe" :style="styleLigneGroupée"/>
                        <td v-for="col in colonnesVisibles" :key="col.key"
                            :class="col.align === 'end' ? 'text-end' : ''"
                            @contextmenu.prevent.stop="ouvrirMenu($event, item, col)">
                            <v-chip v-if="col.type === 'statut'" size="x-small" :color="couleurStatut(item)"
                                variant="tonal">
                                {{ item.statutLibellé }}
                            </v-chip>
                            <v-chip v-else-if="col.type === 'etat'" size="x-small" :color="couleurÉtat(item)"
                                variant="tonal">
                                {{ item.étatLibellé }}
                            </v-chip>
                            <span v-else :class="col.type === 'nombre' || col.type === 'poids' ? 'font-weight-medium' : ''">
                                <v-icon v-if="anomalieCellule(item, col)" size="14" class="me-1"
                                    :icon="anomalieCellule(item, col)?.icone"
                                    :color="anomalieCellule(item, col)?.couleur"/>
                                {{ texteCellule(item, col) }}
                            </span>

                            <!-- Survol de la cellule de poids : les chiffres qui motivent le signalement -->
                            <v-tooltip v-if="anomalieCellule(item, col)" activator="parent" location="top"
                                open-delay="120" max-width="340">
                                <div class="d-flex align-center font-weight-bold mb-1" style="gap: 6px;">
                                    <v-icon size="16" :icon="anomalieCellule(item, col)?.icone"/>
                                    {{ anomalieCellule(item, col)?.titre }}
                                </div>
                                <div v-for="d in anomalieCellule(item, col)?.détails" :key="d.libellé"
                                    class="d-flex justify-space-between text-caption" style="gap: 16px;">
                                    <span style="opacity: 0.7;">{{ d.libellé }}</span>
                                    <span class="font-weight-medium text-no-wrap">{{ d.valeur }}</span>
                                </div>
                            </v-tooltip>
                        </td>
                    </tr>
                </template>

                <template #no-data>
                    <div class="pa-8 text-center text-medium-emphasis">
                        <v-icon icon="mdi-magnify-close" size="48" class="mb-2"/>
                        <div>Aucune palette pour ces filtres.</div>
                    </div>
                </template>
            </v-data-table>
        </div>

        <!-- Menu contextuel (clic droit) -->
        <v-menu v-model="menu.show" :target="[menu.x, menu.y]" location="bottom start">
            <v-card rounded="lg" min-width="260">
                <v-list density="compact">
                    <v-list-subheader class="text-truncate">
                        {{ menu.libellé }} : {{ menu.valeurAffichée }}
                    </v-list-subheader>
                    <v-divider/>
                    <v-list-item prepend-icon="mdi-filter-plus-outline" title="Filtrer sur cette valeur"
                        @click="filtrerDepuisMenu"/>
                    <v-list-item prepend-icon="mdi-filter-minus-outline" title="Exclure cette valeur"
                        @click="exclureDepuisMenu"/>
                    <v-list-item v-if="menuGroupable" prepend-icon="mdi-format-list-group"
                        title="Grouper par ce champ" @click="grouperDepuisMenu"/>
                    <v-list-item v-if="menuTraçable" prepend-icon="mdi-source-branch"
                        title="Tracer uniquement cette valeur"
                        subtitle="Retire les autres filtres et les dates" @click="tracerDepuisMenu"/>
                    <v-divider/>
                    <v-list-item prepend-icon="mdi-content-copy" title="Copier la valeur" @click="copierDepuisMenu"/>
                </v-list>
            </v-card>
        </v-menu>
    </v-card>

    <!-- Détail d'une palette -->
    <v-dialog v-model="dialogDétail" max-width="720" :fullscreen="smAndDown">
        <v-card v-if="paletteDétail" rounded="xl">
            <v-card-title class="d-flex align-center" style="gap: 10px;">
                <v-avatar color="primary" variant="tonal" size="36" rounded="lg">
                    <v-icon icon="mdi-package-variant-closed" size="20"/>
                </v-avatar>
                <div style="flex: 1;">
                    <div class="text-subtitle-1 font-weight-bold">Palette {{ paletteDétail.paletteId }}</div>
                    <div class="text-caption text-medium-emphasis">{{ paletteDétail.produit }}</div>
                </div>
                <v-btn icon="mdi-close" variant="text" size="small" @click="dialogDétail = false"/>
            </v-card-title>

            <v-divider/>

            <v-card-text>
                <v-row dense>
                    <v-col v-for="champ in champsDétail" :key="champ.libellé" cols="12" sm="6">
                        <div class="text-caption text-medium-emphasis">{{ champ.libellé }}</div>
                        <div class="text-body-2 font-weight-medium">{{ champ.valeur }}</div>
                    </v-col>
                </v-row>
            </v-card-text>

            <v-divider/>

            <v-card-actions class="flex-wrap" style="gap: 6px;">
                <v-btn variant="tonal" color="primary" size="small" rounded="pill" prepend-icon="mdi-source-branch"
                    @click="tracerDepuisDétail('noLots', paletteDétail.noLot)">
                    Tracer le lot
                </v-btn>
                <v-btn variant="tonal" color="primary" size="small" rounded="pill" prepend-icon="mdi-cart-arrow-down"
                    @click="tracerDepuisDétail('noCommandesAchat', paletteDétail.noCommandeAchat)">
                    Commande d'achat
                </v-btn>
                <v-btn v-if="paletteDétail.noCommandeVente && paletteDétail.noCommandeVente !== '0'" variant="tonal"
                    color="primary" size="small" rounded="pill" prepend-icon="mdi-truck-outline"
                    @click="tracerDepuisDétail('noCommandesVente', paletteDétail.noCommandeVente)">
                    Commande de vente
                </v-btn>
                <v-btn v-if="paletteDétail.poClient" variant="tonal" color="primary" size="small" rounded="pill"
                    prepend-icon="mdi-file-document-outline" @click="tracerDepuisDétail('poClient', paletteDétail.poClient)">
                    PO client
                </v-btn>
                <v-btn variant="tonal" color="primary" size="small" rounded="pill" prepend-icon="mdi-package-variant"
                    @click="tracerDepuisDétail('codesProduits', paletteDétail.codeProduit)">
                    Produit
                </v-btn>
                <v-spacer/>
                <v-btn variant="text" size="small" rounded="pill" @click="dialogDétail = false">Fermer</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>
/* ── Page header ─────────────────────────── */
.page-header {
    background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}

/* Sur mobile, la recherche passe seule sur sa ligne : le reste des actions, en icône seule,
   tient sur une ou deux lignes compactes au lieu d'étaler le tableau hors de l'écran. */
@media (max-width: 600px) {
    /* Comme la page Carte : la carte occupe tout le viewport, sans marge ni coins arrondis. */
    .page-card {
        height: calc(100vh - var(--v-layout-top, 64px) - var(--v-layout-bottom, 0px)) !important;
        margin: 0 !important;
        border-radius: 0 !important;
    }
    .page-header {
        padding-top: 12px !important;
        padding-bottom: 8px !important;
    }
    .recherche-rapide {
        flex: 1 1 100%;
        order: -1;
        min-width: 0 !important;
        max-width: none !important;
    }
    /* Boutons resserrés et répartis sur toute la largeur : ils tiennent alors sur une seule
       ligne, sans bouton orphelin renvoyé à la ligne suivante. */
    .page-actions {
        width: 100%;
        justify-content: space-between;
        gap: 4px !important;
    }
    .page-actions > .v-btn {
        flex: 0 0 auto;
        min-width: 0;
        padding-inline: 10px;
    }
    .groupement-bar {
        gap: 6px !important;
        margin-top: 8px !important;
    }
    /* Les puces de niveaux occupent leur propre ligne plutôt que de s'intercaler entre les boutons. */
    .fil-groupement {
        flex: 1 1 100%;
    }
}

/* La carte a une hauteur fixe : sans base flexible figée, l'alerte est écrasée jusqu'à
   ce que son texte disparaisse. */
.alerte-limite {
    flex: 0 0 auto;
}

/* ── Zone de filtres ─────────────────────── */
.filtres-zone {
    background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.12)" : "rgba(var(--v-theme-primary), 0.02)"');
}

.filtre-groupe + .filtre-groupe {
    margin-top: 14px;
}

/* Titres de section dans une marge gauche : chaque groupe tient sur une seule ligne. */
.filtres-grille {
    display: grid;
    grid-template-columns: 108px minmax(0, 1fr);
    column-gap: 12px;
    row-gap: 8px;
    align-items: center;
}

.filtre-libellé {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    opacity: 0.6;
}

.filtre-contenu {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}

.filtre-contenu > .filtre-champ {
    flex: 1 1 190px;
    min-width: 0;
}

.filtre-groupe__entete {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 6px;
}

.filtre-groupe__titre {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    opacity: 0.6;
}

/* Sur mobile, les filtres empilés dépassent l'écran : on les borne et on les fait défiler
   pour qu'une partie du tableau reste toujours visible. */
@media (max-width: 600px) {
    .filtres-zone {
        max-height: 45vh;
        overflow-y: auto;
    }
}

/* ── Bandeau de totaux ───────────────────── */
.stats-bar {
    background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.18)" : "rgba(var(--v-theme-primary), 0.05)"');
}

.stat-bloc {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 80px;
}

/* ── Numéro de niveau de regroupement ────── */
.niveau-num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 16px;
    height: 16px;
    border-radius: 50%;
    background: rgba(var(--v-theme-primary), 0.22);
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
}

.niveau-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
}

/* ── Lignes du tableau ───────────────────── */
.palette-row {
    cursor: pointer;
}

.palette-row:hover {
    background: rgba(var(--v-theme-primary), 0.06);
}

/* ── Anomalies de poids ──────────────────── */
.ligne-poids-aberrant {
    background: rgba(251, 140, 0, 0.11);
}

.ligne-poids-aberrant:hover {
    background: rgba(251, 140, 0, 0.20);
}

.ligne-poids-nul {
    background: rgba(229, 57, 53, 0.11);
}

.ligne-poids-nul:hover {
    background: rgba(229, 57, 53, 0.20);
}

.groupe-row {
    /* Le fond est posé en ligne : il dépend du niveau. */
    border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

/*
 * Vuetify ajoute une colonne de pliage sans largeur dès qu'un regroupement est actif : elle
 * absorbe alors tout l'espace restant sur écran large. On la réduit au rail de hiérarchie.
 */
.tableau-groupé :deep(thead th:first-child),
.tableau-groupé :deep(td.td-groupe) {
    width: var(--largeur-colonne-groupe);
    min-width: var(--largeur-colonne-groupe);
    max-width: var(--largeur-colonne-groupe);
    padding: 0 !important;
}

.tableau-groupé :deep(thead th:first-child) {
    /* Masque le libellé « Group » sans retirer la cellule, requise pour l'alignement. */
    font-size: 0;
}

.total-row {
    background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.35)" : "rgba(var(--v-theme-primary), 0.08)"');
}

/* ── Vue sommaire hiérarchique ───────────── */
.sommaire-tableau {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
}

.sommaire-tableau th {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 8px 12px;
    background: rgb(var(--v-theme-surface));
    font-size: 0.75rem;
    font-weight: 700;
    text-align: end;
    white-space: nowrap;
    border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.sommaire-tableau th:first-child {
    text-align: start;
}

.sommaire-tableau td {
    padding: 3px 12px;
    white-space: nowrap;
    border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

/* Le libellé absorbe l'espace restant : les colonnes de chiffres restent alignées à droite. */
.sommaire-libellé {
    width: 100%;
}

/* Réserve la place du chevron sur les feuilles, sinon leur libellé se décale vers la gauche. */
.sommaire-puce {
    display: inline-block;
    width: 24px;
}

.entete-impression {
    display: none;
}

/* ── Impression du sommaire ──────────────── */
@media print {
    .page-header,
    .filtres-zone,
    .stats-bar,
    .alerte-limite,
    .groupement-bar,
    .sommaire-actions {
        display: none !important;
    }

    .page-card {
        height: auto !important;
        margin: 0 !important;
        border: none !important;
        box-shadow: none !important;
    }

    .contenu-vues {
        overflow: visible !important;
    }

    .entete-impression {
        display: block;
        margin-bottom: 10px;
    }

    .entete-impression__titre {
        font-size: 14pt;
        font-weight: 700;
    }

    .entete-impression__meta {
        font-size: 8pt;
        opacity: 0.75;
    }

    .sommaire-tableau {
        font-size: 9pt;
    }

    /*
     * Rendu noir et blanc : la hiérarchie passe par le retrait, la graisse et les filets.
     * Les teintes et rails de niveau sont posés en style inline, d'où les `!important`.
     */
    .sommaire-tableau,
    .sommaire-tableau th,
    .sommaire-tableau td,
    .sommaire-tableau tr,
    .sommaire-tableau *,
    .entete-impression,
    .entete-impression * {
        background: none !important;
        box-shadow: none !important;
        color: #000 !important;
    }

    /* Les chevrons ne servent à rien sur papier, mais gardent leur place pour l'alignement. */
    .sommaire-libellé .v-btn {
        visibility: hidden;
    }

    .sommaire-tableau th,
    .sommaire-tableau td {
        border-color: #999 !important;
    }

    .sommaire-tableau th {
        border-bottom: 1.5pt solid #000 !important;
    }

    /* Un filet marque le début de chaque bloc de premier niveau. */
    .sommaire-niveau-0 > td {
        border-top: 1pt solid #000 !important;
    }

    .niveau-badge {
        border: 0.5pt solid #000;
    }

    .entete-impression__meta {
        opacity: 1;
    }

    .total-row > td {
        border-top: 1.5pt solid #000 !important;
    }

    /* L'entête se répète sur chaque feuille et aucune ligne n'est coupée en deux. */
    .sommaire-tableau thead {
        display: table-header-group;
    }

    .sommaire-tableau th {
        position: static;
    }

    .sommaire-ligne {
        break-inside: avoid;
    }
}
</style>

<!--
    Non `scoped` : à l'impression il faut neutraliser la chrome applicative (barre, tiroir, pied)
    et les conteneurs à hauteur fixe de la mise en page, qui vivent hors de ce composant.
-->
<style>
@media print {
    .v-application,
    .v-application__wrap,
    .v-layout,
    .v-main {
        display: block !important;
        height: auto !important;
        min-height: 0 !important;
        padding: 0 !important;
        overflow: visible !important;
    }

    .v-app-bar,
    .v-navigation-drawer,
    .v-footer,
    .v-overlay-container {
        display: none !important;
    }
}
</style>
